// src/components/FoundationReportGenerator.jsx
//
// Générateur de rapport annuel au Conseil d'Administration — mode Fondation uniquement.
//
// Exports : PDF (jsPDF) + Word (docx) + Excel (xlsx via SheetJS)
//
// Route suggérée : /foundation-report
// Accès : plan family_office + foundation_mode = true
//
// Données chargées :
//   - collections (tous les types foundation_*)
//   - artworks via artworks_secure
//   - collection_third_parties (dépôts / prêts)
//   - foundation_annual_reports (historique)
//
// Snapshot sauvegardé dans foundation_annual_reports.snapshot_json
//

import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabaseClient";
import { useFT } from "../locales/foundationCopy";
import { useAuth } from "../context/AuthContext";

// ─── Helpers formatage ────────────────────────────────────────────────────────

// Langue des documents générés (PDF / Word / Excel) : fixée au début de chaque générateur
let DOC_LOCALE = "fr-FR";

function fmtEur(n) {
  if (!n && n !== 0) return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  // jsPDF ne supporte pas les espaces insécables ni les caractères Unicode complexes
  return num.toLocaleString(DOC_LOCALE, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " EUR";
}

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(DOC_LOCALE); }
  catch { return d; }
}

function fmtDateLong(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(DOC_LOCALE, {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return d; }
}

const COLLECTION_TYPE_LABELS = {
  foundation_owned:    "Propriété propre",
  foundation_founder:  "Prêt fondateur",
  foundation_donation: "Donation / legs",
  foundation_deposit:  "Dépôt institution",
};

const COLLECTION_TYPE_COLORS_PDF = {
  foundation_owned:    [44, 95, 74],
  foundation_founder:  [122, 92, 0],
  foundation_donation: [139, 69, 19],
  foundation_deposit:  [28, 58, 94],
};

// ─── Styles PDF communs ───────────────────────────────────────────────────────

const PDF_COLORS = {
  black:   [26, 26, 26],
  gold:    [201, 169, 97],
  grey1:   [107, 107, 107],
  grey2:   [200, 196, 190],
  cream:   [249, 247, 244],
  white:   [255, 255, 255],
};

// ─── Composant principal ──────────────────────────────────────────────────────

export default function FoundationReportGenerator() {
  const { user, profile } = useAuth();
  const { T, locale } = useFT();
  // Formats d'affichage à l'écran (les documents générés utilisent fmtEur / fmtDate, réglés par DOC_LOCALE)
  const fmtEurUI = (n) => (n || n === 0 ? `${Number(n).toLocaleString(locale, { maximumFractionDigits: 0 })} EUR` : "—");
  const fmtDateUI = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(locale); } catch { return d; } };

  // ── State ──────────────────────────────────────────────────────────────────
  const [isLoading,    setIsLoading]    = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error,        setError]        = useState(null);
  const [success,      setSuccess]      = useState(null);

  // Données
  const [collections,   setCollections]   = useState([]);
  const [artworksByCol, setArtworksByCol] = useState({}); // { [collection_id]: artworks[] }
  const [thirdParties,  setThirdParties]  = useState({}); // { [collection_id]: thirdParty }
  const [pastReports,   setPastReports]   = useState([]);

  // Formulaire rapport
  const currentYear = new Date().getFullYear();
  const [reportYear,     setReportYear]     = useState(currentYear - 1); // exercice passé par défaut
  const [directorName,   setDirectorName]   = useState("");
  const [directorNote,   setDirectorNote]   = useState("");
  const [foundationName, setFoundationName] = useState("");
  const [foundationLogo, setFoundationLogo] = useState(null); // base64
  const logoInputRef = useRef(null);

  // ── State MODE CORPORATE (Art Advisor → client entreprise) ────────────────
  // Les 4 champs corporate sont stockés en localStorage (pas encore en base)
  // clé : artkiv_corp_fields → { [artworkId]: { lieu_accrochage, statut_juridique, usage, visibilite } }
  const [corpFields, setCorpFields] = useState(() => {
    try { return JSON.parse(localStorage.getItem("artkiv_corp_fields") || "{}"); }
    catch { return {}; }
  });
  const [allCollections,   setAllCollections]   = useState([]);
  const [selectedColId,    setSelectedColId]    = useState("");
  const [corpArtworks,     setCorpArtworks]     = useState([]);
  const [corpLoading,      setCorpLoading]      = useState(false);
  const [corpClientName,   setCorpClientName]   = useState("");
  // Propriétaire (client) de chaque collection partagée avec l'advisor, et dernier nom prérempli automatiquement
  const [colOwnerById,     setColOwnerById]     = useState({});
  const autoClientName = useRef("");
  const [corpAdvisorName,  setCorpAdvisorName]  = useState("");
  const [corpAuditNotes,   setCorpAuditNotes]   = useState("");
  const [corpGenerating,   setCorpGenerating]   = useState(false);
  const [corpError,        setCorpError]        = useState(null);
  const [corpSuccess,      setCorpSuccess]      = useState(null);

  // ── Mode détection ────────────────────────────────────────────────────────
  // foundation_mode  → rapport annuel CA (comportement historique)
  // Art Advisor / autres → audit corporate pour client entreprise
  const isCorporateMode = profile ? !profile.foundation_mode : false;

  // ── Load collections (corporate mode) ─────────────────────────────────────
  useEffect(() => {
    if (!user?.id || !isCorporateMode) return;
    (async () => {
      // Collections de l'advisor + celles d'un client qui lui a donné accès (mandat : lecture ou modification).
      const { data: own } = await supabase.from("collections").select("id,title").eq("client_id", user.id);
      let shared = [];
      const { data: perms } = await supabase
        .from("collection_permissions")
        .select("collection_id,client_id,can_view,can_edit")
        .eq("grantee_id", user.id);
      setColOwnerById(Object.fromEntries((perms || []).map((p) => [p.collection_id, p.client_id])));
      const sharedIds = (perms || []).filter((p) => p.can_view || p.can_edit).map((p) => p.collection_id);
      if (sharedIds.length) {
        const { data: sc } = await supabase.from("collections").select("id,title").in("id", sharedIds);
        shared = (sc || []).map((c) => ({ ...c, title: `${c.title} (client)` }));
      }
      const seen = new Set();
      const cols = [...(own || []), ...shared].filter((c) => !seen.has(c.id) && seen.add(c.id));
      setAllCollections(cols);
      if (cols.length > 0) setSelectedColId(cols[0].id);
      if (profile?.first_name || profile?.last_name) {
        setCorpAdvisorName(`${profile.first_name || ""} ${profile.last_name || ""}`.trim());
      }
    })();
  }, [user?.id, isCorporateMode, profile]);

  // Préremplit le nom du client depuis la collection sélectionnée (client accepté de l'advisor).
  // Un nom saisi à la main n'est jamais écrasé.
  useEffect(() => {
    if (!selectedColId || !isCorporateMode) return;
    const clientId = colOwnerById[selectedColId];
    let alive = true;
    (async () => {
      let name = "";
      if (clientId && clientId !== user?.id) {
        const { data } = await supabase.rpc("expert_my_accepted_clients");
        const c = (data || []).find((x) => x.client_id === clientId);
        name = c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() : "";
      }
      if (!alive) return;
      setCorpClientName((cur) => {
        if (cur && cur !== autoClientName.current) return cur;
        autoClientName.current = name;
        return name;
      });
    })();
    return () => { alive = false; };
  }, [selectedColId, colOwnerById, isCorporateMode, user?.id]);

  useEffect(() => {
    if (!selectedColId || !isCorporateMode) return;
    (async () => {
      setCorpLoading(true);
      const { data } = await supabase
        .from("artworks_secure")
        .select([
          "id", "title", "artist", "year", "medium", "medium_normalized_key",
          "dimensions", "inventory_number", "condition",
          "current_estimated_value", "valuation_consensus",
          "valuation_market_value", "estimated_value",
          "artwork_status", "image_url",
        ].join(","))
        .eq("collection_id", selectedColId);
      setCorpArtworks(data || []);
      setCorpLoading(false);
    })();
  }, [selectedColId, isCorporateMode]);

  // ── Persistance des champs corporate ──────────────────────────────────────
  // Source de vérité : table artwork_corporate_fields. Le navigateur (localStorage) ne sert plus que de
  // secours si la base est injoignable ; les valeurs qui n'existent que localement y sont reprises une fois.
  const CORP_KEYS = ["lieu_accrochage", "statut_juridique", "usage", "visibilite"];
  const [corpSave, setCorpSave] = useState("idle"); // idle | saving | saved | error
  const corpTimers = useRef({});
  const corpFieldsRef = useRef(corpFields);
  useEffect(() => { corpFieldsRef.current = corpFields; }, [corpFields]);

  const saveCorpRow = async (artworkId) => {
    const row = corpFieldsRef.current[artworkId] || {};
    const payload = { artwork_id: artworkId };
    CORP_KEYS.forEach((k) => { payload[k] = (row[k] || "").trim() || null; });
    const { error } = await supabase.from("artwork_corporate_fields").upsert(payload, { onConflict: "artwork_id" });
    return !error;
  };

  useEffect(() => {
    if (!isCorporateMode || corpArtworks.length === 0) return;
    let alive = true;
    (async () => {
      const ids = corpArtworks.map((a) => a.id);
      const { data, error } = await supabase.from("artwork_corporate_fields").select("*").in("artwork_id", ids);
      if (!alive || error) return;
      const fromDb = {};
      (data || []).forEach((r) => {
        fromDb[r.artwork_id] = Object.fromEntries(CORP_KEYS.map((k) => [k, r[k] || ""]));
      });
      const local = corpFieldsRef.current;
      const toPush = ids.filter((id) => !fromDb[id] && CORP_KEYS.some((k) => local[id]?.[k]));
      setCorpFields((prev) => ({ ...prev, ...fromDb }));
      for (const id of toPush) { await saveCorpRow(id); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpArtworks, isCorporateMode]);

  const updateCorpField = (artworkId, field, value) => {
    setCorpFields(prev => {
      const updated = { ...prev, [artworkId]: { ...(prev[artworkId] || {}), [field]: value } };
      try { localStorage.setItem("artkiv_corp_fields", JSON.stringify(updated)); } catch {}
      return updated;
    });
    setCorpSave("saving");
    clearTimeout(corpTimers.current[artworkId]);
    corpTimers.current[artworkId] = setTimeout(async () => {
      setCorpSave((await saveCorpRow(artworkId)) ? "saved" : "error");
    }, 600);
  };

  const getCorpField = (artworkId, field) => corpFields[artworkId]?.[field] || "";

  const getCorpEstVal = (aw) =>
    Number(aw.current_estimated_value || aw.valuation_consensus || aw.valuation_market_value || aw.estimated_value || 0);

  // ══════════════════════════════════════════════════════════════════════════
  // PDF AUDIT COLLECTION CORPORATE
  // ══════════════════════════════════════════════════════════════════════════
  async function generateCorporateAuditPDF() {
    const { default: jsPDF } = await import("jspdf");
    DOC_LOCALE = locale;
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, M = 18, CW = W - M * 2;
    let y = 0;
    const addPage = () => { doc.addPage(); y = M; };
    const checkY = (n = 20) => { if (y + n > 280) addPage(); };
    const sf = (size, style = "normal", color = PDF_COLORS.black) => {
      doc.setFontSize(size); doc.setFont("helvetica", style); doc.setTextColor(...color);
    };

    const today = new Date().toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    const totalVal = corpArtworks.reduce((s, a) => s + getCorpEstVal(a), 0);

    // ── PAGE DE GARDE ────────────────────────────────────────────────────────
    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(0, 0, W, 297, "F");
    doc.setFillColor(...PDF_COLORS.gold);
    doc.rect(M, 72, 2, 56, "F");

    sf(26, "bold", PDF_COLORS.white);
    doc.text(T("DIAGNOSTIC"), M + 8, 88);
    doc.text(T("COLLECTION CORPORATE"), M + 8, 100);
    sf(14, "normal", PDF_COLORS.gold);
    doc.text(T("Audit · Inventaire · Évaluation · Recommandations"), M + 8, 114);

    if (corpClientName) {
      sf(12, "bold", [200, 200, 200]);
      doc.text(corpClientName, M + 8, 130);
    }
    doc.setDrawColor(...PDF_COLORS.gold);
    doc.setLineWidth(0.3);
    doc.line(M, 210, W - M, 210);

    sf(9, "normal", [120, 120, 120]);
    doc.text(T("Rapport établi le {date}", { date: today }), M, 220);
    if (corpAdvisorName) doc.text(T("Art Advisor : {name}", { name: corpAdvisorName }), M, 228);
    doc.text(T("Document confidentiel — Usage interne"), M, 260);
    doc.text(T("Artkiv — Conseil en art"), W - M, 260, { align: "right" });

    // ── SOMMAIRE ─────────────────────────────────────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    sf(9, "normal", PDF_COLORS.grey1);
    doc.text(T("SOMMAIRE"), M, y + 6); y += 14;
    sf(20, "bold", PDF_COLORS.black);
    doc.text(T("Table des matières"), M, y); y += 10;
    doc.setDrawColor(...PDF_COLORS.gold);
    doc.setLineWidth(0.5);
    doc.line(M, y, M + 30, y); y += 12;

    [
      T("1. Synthèse de la collection"),
      T("2. Inventaire détaillé avec champs corporate"),
      T("3. Répartition par usage et lieu d'accrochage"),
      T("4. État documentaire et risques"),
      T("5. Recommandations assurance & conservation"),
      T("6. Note de l'Art Advisor"),
    ].forEach((item, i) => {
      sf(12, "normal", PDF_COLORS.black);
      doc.text(item, M + 4, y);
      sf(12, "normal", PDF_COLORS.grey1);
      doc.text(`${i + 3}`, W - M - 4, y, { align: "right" });
      y += 9;
    });

    // ── SECTION 1 : SYNTHÈSE ─────────────────────────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = M;

    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(M, y, CW, 12, "F");
    sf(10, "bold", PDF_COLORS.white);
    doc.text(T("1. SYNTHÈSE DE LA COLLECTION"), M + 4, y + 8);
    y += 18;

    if (corpClientName) {
      sf(7, "normal", PDF_COLORS.grey1);
      doc.text(T("CLIENT"), M, y);
      sf(13, "bold", PDF_COLORS.black);
      doc.text(corpClientName, M, y + 6);
      y += 16;
    }

    // Cartes stats
    const cardW = (CW - 8) / 3;
    const statCards = [
      { label: T("TOTAL OEUVRES"), value: String(corpArtworks.length) },
      { label: T("VALEUR ESTIMÉE"), value: totalVal > 0 ? fmtEur(totalVal) : T("Non valorisée") },
      { label: T("AVEC DOCUMENTATION"), value: String(corpArtworks.filter(a => a.condition && a.condition !== "unknown").length) },
    ];
    statCards.forEach((card, i) => {
      const cx = M + i * (cardW + 4);
      doc.setFillColor(255, 255, 255);
      doc.rect(cx, y, cardW, 26, "F");
      doc.setDrawColor(...PDF_COLORS.grey2);
      doc.setLineWidth(0.3);
      doc.rect(cx, y, cardW, 26, "S");
      doc.setFillColor(...PDF_COLORS.gold);
      doc.rect(cx, y, cardW, 1.5, "F");
      sf(6.5, "normal", PDF_COLORS.grey1);
      doc.text(card.label, cx + 3, y + 7);
      sf(13, "bold", PDF_COLORS.black);
      doc.text(card.value, cx + 3, y + 18);
    });
    y += 34;

    // Répartition statut juridique
    sf(11, "bold", PDF_COLORS.black);
    doc.text(T("Répartition par statut juridique"), M, y); y += 8;

    const statutCounts = {};
    corpArtworks.forEach(aw => {
      const s = getCorpField(aw.id, "statut_juridique") || "Non renseigné";
      statutCounts[s] = (statutCounts[s] || 0) + 1;
    });

    const statutColors = {
      "Propriété entreprise": [44, 95, 74],
      "Prêt":                 [122, 92, 0],
      "Dépôt":                [28, 58, 94],
      "Donation":             [139, 69, 19],
      "Œuvre en commande":    [80, 40, 100],
      "Non renseigné":        [150, 150, 150],
    };

    Object.entries(statutCounts).forEach(([label, count]) => {
      checkY(10);
      const pct = Math.round((count / (corpArtworks.length || 1)) * 100);
      const barW = Math.max(1, Math.round((count / (corpArtworks.length || 1)) * (CW - 80)));
      const color = statutColors[label] || PDF_COLORS.grey1;
      sf(8.5, "normal", PDF_COLORS.black);
      doc.text(T(label).substring(0, 24), M, y + 5);
      sf(8.5, "bold", PDF_COLORS.black);
      doc.text(`${count}  (${pct}%)`, M + 72, y + 5);
      doc.setFillColor(...PDF_COLORS.grey2);
      doc.rect(M + 102, y, CW - 104, 5, "F");
      if (count > 0) { doc.setFillColor(...color); doc.rect(M + 102, y, barW, 5, "F"); }
      y += 11;
    });

    // ── SECTION 2 : INVENTAIRE AVEC CHAMPS CORPORATE ─────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = M;

    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(M, y, CW, 12, "F");
    sf(10, "bold", PDF_COLORS.white);
    doc.text(T("2. INVENTAIRE DÉTAILLÉ — CHAMPS CORPORATE"), M + 4, y + 8);
    y += 18;

    // Tableau avec les 4 champs corporate
    const colWs = [8, 30, 22, 10, 20, 22, 22, 20];
    const hdrs = [T("N°"), T("Titre"), T("Artiste"), T("An."), T("Lieu accrochage"), T("Statut juridique"), T("Usage"), T("Visibilité")];
    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(M, y, CW, 6, "F");
    let xp = M + 1;
    hdrs.forEach((h, i) => { sf(5.5, "bold", PDF_COLORS.white); doc.text(h, xp, y + 4.5); xp += colWs[i]; });
    y += 6;

    corpArtworks.forEach((aw, idx) => {
      checkY(7);
      const bg = idx % 2 === 0 ? [255, 255, 255] : [249, 247, 244];
      doc.setFillColor(...bg);
      doc.rect(M, y, CW, 6.5, "F");
      xp = M + 1;
      const row = [
        (aw.inventory_number || "—").substring(0, 5),
        (aw.title || T("Sans titre")).substring(0, 18),
        (aw.artist || "—").substring(0, 14),
        String(aw.year || "—").substring(0, 4),
        T(getCorpField(aw.id, "lieu_accrochage")).substring(0, 13) || "—",
        T(getCorpField(aw.id, "statut_juridique")).substring(0, 14) || "—",
        T(getCorpField(aw.id, "usage")).substring(0, 13) || "—",
        T(getCorpField(aw.id, "visibilite")).substring(0, 12) || "—",
      ];
      row.forEach((val, i) => { sf(5.5, "normal", PDF_COLORS.black); doc.text(val, xp, y + 5); xp += colWs[i]; });
      y += 6.5;
    });

    // ── SECTION 3 : RÉPARTITION PAR USAGE ET LIEU ────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = M;

    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(M, y, CW, 12, "F");
    sf(10, "bold", PDF_COLORS.white);
    doc.text(T("3. RÉPARTITION PAR USAGE ET LIEU D'ACCROCHAGE"), M + 4, y + 8);
    y += 18;

    const buildDistrib = (field, title) => {
      const counts = {};
      corpArtworks.forEach(aw => {
        const v = getCorpField(aw.id, field) || "Non renseigné";
        counts[v] = (counts[v] || 0) + 1;
      });
      sf(10, "bold", PDF_COLORS.black);
      doc.text(title, M, y); y += 8;
      Object.entries(counts).sort(([, a], [, b]) => b - a).forEach(([label, count]) => {
        checkY(9);
        const pct = Math.round((count / (corpArtworks.length || 1)) * 100);
        sf(8, "normal", PDF_COLORS.black);
        doc.text(T(label).substring(0, 28), M + 4, y + 5);
        sf(8, "bold", PDF_COLORS.black);
        doc.text(`${count}  ${pct}%`, M + CW - 24, y + 5, { align: "right" });
        const bw = Math.max(1, Math.round((count / (corpArtworks.length || 1)) * 60));
        doc.setFillColor(...PDF_COLORS.grey2);
        doc.rect(M + CW - 22, y + 1, 22, 4, "F");
        doc.setFillColor(...PDF_COLORS.gold);
        doc.rect(M + CW - 22, y + 1, bw, 4, "F");
        y += 9;
      });
      y += 8;
    };

    buildDistrib("usage", T("Répartition par usage"));
    buildDistrib("lieu_accrochage", T("Répartition par lieu d'accrochage"));
    buildDistrib("visibilite", T("Répartition par visibilité"));

    // ── SECTION 4 : ÉTAT DOCUMENTAIRE ET RISQUES ─────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = M;

    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(M, y, CW, 12, "F");
    sf(10, "bold", PDF_COLORS.white);
    doc.text(T("4. ÉTAT DOCUMENTAIRE ET RISQUES"), M + 4, y + 8);
    y += 18;

    const condMap = { excellent: "Excellent", very_good: "Très bon", good: "Bon", fair: "Moyen", poor: "Mauvais", damaged: "Endommagé" };
    const condCounts = {};
    corpArtworks.forEach(aw => {
      const c = condMap[aw.condition] || aw.condition || "Non renseigné";
      condCounts[c] = (condCounts[c] || 0) + 1;
    });

    sf(10, "bold", PDF_COLORS.black);
    doc.text(T("État de conservation"), M, y); y += 8;
    const condColors = { "Excellent": [44, 122, 75], "Très bon": [44, 122, 75], "Bon": [100, 160, 100], "Moyen": [201, 169, 97], "Mauvais": [184, 64, 64], "Endommagé": [184, 64, 64], "Non renseigné": [150, 150, 150] };
    Object.entries(condCounts).forEach(([label, count]) => {
      checkY(9);
      const pct = Math.round((count / (corpArtworks.length || 1)) * 100);
      const bw = Math.max(1, Math.round((count / (corpArtworks.length || 1)) * (CW - 80)));
      doc.setFillColor(...(condColors[label] || PDF_COLORS.grey1));
      doc.rect(M, y + 1.5, 3, 4, "F");
      sf(8, "normal", PDF_COLORS.black);
      doc.text(T(label), M + 6, y + 5);
      sf(8, "bold", PDF_COLORS.black);
      doc.text(`${count}  (${pct}%)`, M + 72, y + 5);
      doc.setFillColor(...PDF_COLORS.grey2);
      doc.rect(M + 100, y, CW - 102, 5, "F");
      if (count > 0) { doc.setFillColor(...(condColors[label] || PDF_COLORS.grey1)); doc.rect(M + 100, y, bw, 5, "F"); }
      y += 10;
    });

    // Alertes
    y += 6;
    sf(10, "bold", PDF_COLORS.black);
    doc.text(T("Points de vigilance"), M, y); y += 8;
    const withoutEstim = corpArtworks.filter(a => !getCorpEstVal(a));
    const withoutStatut = corpArtworks.filter(a => !getCorpField(a.id, "statut_juridique"));
    const poorCond = corpArtworks.filter(a => ["poor", "damaged", "heavily_damaged"].includes(a.condition));

    [
      withoutEstim.length > 0 ? T("{n} oeuvre(s) sans estimation de valeur", { n: withoutEstim.length }) : null,
      withoutStatut.length > 0 ? T("{n} oeuvre(s) sans statut juridique défini", { n: withoutStatut.length }) : null,
      poorCond.length > 0 ? T("{n} oeuvre(s) en mauvais état nécessitant attention", { n: poorCond.length }) : null,
      T("{n} oeuvre(s) sans lieu d'accrochage renseigné", { n: corpArtworks.filter(a => !getCorpField(a.id, "lieu_accrochage")).length }),
    ].filter(Boolean).forEach(alert => {
      checkY(9);
      doc.setFillColor(254, 243, 235);
      doc.rect(M, y, CW, 8, "F");
      doc.setFillColor(201, 100, 30);
      doc.rect(M, y, 2, 8, "F");
      sf(8, "normal", PDF_COLORS.black);
      doc.text(`⚠  ${alert}`, M + 6, y + 5.5);
      y += 10;
    });

    // ── SECTION 5 : RECOMMANDATIONS ASSURANCE & CONSERVATION ─────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = M;

    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(M, y, CW, 12, "F");
    sf(10, "bold", PDF_COLORS.white);
    doc.text(T("5. RECOMMANDATIONS ASSURANCE & CONSERVATION"), M + 4, y + 8);
    y += 18;

    const reccos = [
      { titre: T("Couverture assurance"), detail: totalVal > 0
          ? T("La collection compte {n} oeuvre(s) pour une valeur estimée de {v}. Une police d'assurance \"clou à clou\" est recommandée couvrant l'ensemble des lieux d'accrochage identifiés.", { n: corpArtworks.length, v: fmtEur(totalVal) })
          : T("La collection compte {n} oeuvre(s). Une police d'assurance \"clou à clou\" est recommandée couvrant l'ensemble des lieux d'accrochage identifiés.", { n: corpArtworks.length }) },
      { titre: T("Inventaire photographique"), detail: T("Il est conseillé de constituer un dossier photographique normalisé (face, revers, détails, signature) pour chaque oeuvre, conservé hors site et mis à jour annuellement.") },
      { titre: T("Constats d'état"), detail: T("Les oeuvres marquées \"Moyen\" ou \"Mauvais\" requièrent un constat d'état par un restaurateur agréé avant tout déplacement ou prêt.") },
      { titre: T("Suivi des dépôts et prêts"), detail: T("Chaque oeuvre en statut Dépôt ou Prêt doit faire l'objet d'une convention signée précisant la durée, les conditions de transport, la couverture assurance et les responsabilités.") },
      { titre: T("Revue annuelle"), detail: T("Une révision annuelle des valeurs estimées est recommandée pour les oeuvres représentant plus de 10% de la valeur totale de la collection.") },
    ];

    reccos.forEach((r, i) => {
      checkY(28);
      doc.setFillColor(255, 255, 255);
      doc.rect(M, y, CW, 24, "F");
      doc.setDrawColor(...PDF_COLORS.grey2);
      doc.setLineWidth(0.2);
      doc.rect(M, y, CW, 24, "S");
      doc.setFillColor(...PDF_COLORS.gold);
      doc.rect(M, y, 2, 24, "F");
      sf(9, "bold", PDF_COLORS.black);
      doc.text(`${i + 1}. ${r.titre}`, M + 6, y + 7);
      sf(8, "normal", PDF_COLORS.grey1);
      const lines = doc.splitTextToSize(r.detail, CW - 12);
      lines.slice(0, 2).forEach((l, li) => { doc.text(l, M + 6, y + 13 + li * 5); });
      y += 28;
    });

    // ── SECTION 6 : NOTE DE L'ART ADVISOR ────────────────────────────────────
    if (corpAuditNotes) {
      addPage();
      doc.setFillColor(...PDF_COLORS.cream);
      doc.rect(0, 0, W, 297, "F");
      y = M;

      doc.setFillColor(...PDF_COLORS.black);
      doc.rect(M, y, CW, 12, "F");
      sf(10, "bold", PDF_COLORS.white);
      doc.text(T("6. NOTE DE L'ART ADVISOR"), M + 4, y + 8);
      y += 18;

      sf(10, "normal", PDF_COLORS.black);
      const noteLines = doc.splitTextToSize(corpAuditNotes, CW);
      noteLines.forEach(l => { checkY(6); doc.text(l, M, y); y += 6; });
    }

    // ── NUMÉROTATION ────────────────────────────────────────────────────────
    const nPages = doc.getNumberOfPages();
    for (let p = 1; p <= nPages; p++) {
      doc.setPage(p);
      if (p === 1) continue;
      sf(7, "normal", PDF_COLORS.grey1);
      doc.text(`${p} / ${nPages}`, W - M, 291, { align: "right" });
      doc.text(T("Diagnostic Collection Corporate") + (corpClientName ? " — " + corpClientName : ""), M, 291);
      doc.setDrawColor(...PDF_COLORS.grey2);
      doc.setLineWidth(0.2);
      doc.line(M, 288, W - M, 288);
    }

    const slug = (corpClientName || T("collection")).replace(/[^a-zA-Z0-9]/g, "-").substring(0, 30);
    doc.save(`${T("audit-corporate-")}${slug}.pdf`);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // PDF RAPPORT DE COHÉRENCE CURATORIALE
  // ══════════════════════════════════════════════════════════════════════════
  async function generateCuratorialPDF() {
    const { default: jsPDF } = await import("jspdf");
    DOC_LOCALE = locale;
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, M = 18, CW = W - M * 2;
    let y = 0;
    const addPage = () => { doc.addPage(); y = M; };
    const checkY = (n = 20) => { if (y + n > 280) addPage(); };
    const sf = (size, style = "normal", color = PDF_COLORS.black) => {
      doc.setFontSize(size); doc.setFont("helvetica", style); doc.setTextColor(...color);
    };
    const today = new Date().toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });

    // ── PAGE DE GARDE ────────────────────────────────────────────────────────
    doc.setFillColor(249, 247, 244);
    doc.rect(0, 0, W, 297, "F");
    doc.setFillColor(...PDF_COLORS.gold);
    doc.rect(M, 68, CW, 1.5, "F");
    doc.rect(M, 140, CW, 1.5, "F");

    sf(9, "normal", PDF_COLORS.grey1);
    doc.text(T("RAPPORT DE COHÉRENCE CURATORIALE"), M, 64);
    sf(30, "bold", PDF_COLORS.black);
    doc.text(T("Analyse de"), M, 84);
    doc.text(T("la collection"), M, 96);
    sf(13, "normal", PDF_COLORS.grey1);
    doc.text(T("Ligne artistique · Cohérence · Recommandations"), M, 112);

    if (corpClientName) {
      sf(12, "bold", PDF_COLORS.black);
      doc.text(corpClientName, M, 150);
    }
    sf(9, "normal", PDF_COLORS.grey1);
    doc.text(corpAdvisorName ? T("Rédigé le {date} par {name}", { date: today, name: corpAdvisorName }) : T("Rédigé le {date}", { date: today }), M, 165);

    // ── SECTION 1 : ANALYSE DE LA COLLECTION ─────────────────────────────────
    addPage();
    doc.setFillColor(249, 247, 244);
    doc.rect(0, 0, W, 297, "F");
    y = M;

    sf(9, "normal", PDF_COLORS.grey1);
    doc.text(T("1 / ANALYSE DE LA COLLECTION"), M, y + 6); y += 14;
    doc.setDrawColor(...PDF_COLORS.gold);
    doc.setLineWidth(0.5);
    doc.line(M, y, M + 30, y); y += 10;

    // Artistes présents
    const artistMap = {};
    corpArtworks.forEach(aw => {
      const a = aw.artist || T("Non attribué");
      artistMap[a] = (artistMap[a] || 0) + 1;
    });
    const topArtists = Object.entries(artistMap).sort(([, a], [, b]) => b - a).slice(0, 10);

    sf(11, "bold", PDF_COLORS.black);
    doc.text(T("{n} oeuvres — {k} artistes représentés", { n: corpArtworks.length, k: Object.keys(artistMap).length }), M, y); y += 12;

    // Tableau artistes
    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(M, y, CW, 7, "F");
    sf(7, "bold", PDF_COLORS.white);
    doc.text(T("Artiste"), M + 3, y + 5);
    doc.text(T("Oeuvres"), M + 100, y + 5);
    doc.text(T("Part de la collection"), M + 125, y + 5);
    y += 7;

    topArtists.forEach(([artist, count], idx) => {
      checkY(7);
      doc.setFillColor(idx % 2 === 0 ? 255 : 249, idx % 2 === 0 ? 255 : 247, idx % 2 === 0 ? 255 : 244);
      doc.rect(M, y, CW, 7, "F");
      sf(7.5, "normal", PDF_COLORS.black);
      doc.text(artist.substring(0, 40), M + 3, y + 5);
      doc.text(String(count), M + 103, y + 5);
      const pct = Math.round((count / (corpArtworks.length || 1)) * 100);
      const bw = Math.round(pct * 0.6);
      doc.setFillColor(...PDF_COLORS.gold);
      doc.rect(M + 125, y + 2, bw, 3, "F");
      sf(7, "normal", PDF_COLORS.grey1);
      doc.text(`${pct}%`, M + 128 + bw, y + 5);
      y += 7;
    });
    y += 10;

    // Répartition par medium
    const mediumMap = {};
    corpArtworks.forEach(aw => {
      const m = aw.medium_normalized_key || aw.medium || T("Non renseigné");
      mediumMap[m] = (mediumMap[m] || 0) + 1;
    });
    const topMediums = Object.entries(mediumMap).sort(([, a], [, b]) => b - a).slice(0, 8);

    checkY(40);
    sf(11, "bold", PDF_COLORS.black);
    doc.text(T("Répartition par technique / médium"), M, y); y += 10;

    topMediums.forEach(([med, count]) => {
      checkY(8);
      const pct = Math.round((count / (corpArtworks.length || 1)) * 100);
      const bw = Math.max(2, Math.round((count / (corpArtworks.length || 1)) * (CW - 60)));
      sf(8, "normal", PDF_COLORS.black);
      doc.text(med.substring(0, 26), M, y + 5);
      doc.setFillColor(...PDF_COLORS.grey2);
      doc.rect(M + 76, y, CW - 80, 5, "F");
      doc.setFillColor(...PDF_COLORS.black);
      doc.rect(M + 76, y, bw, 5, "F");
      sf(7.5, "normal", PDF_COLORS.grey1);
      doc.text(`${count}  (${pct}%)`, M + 78 + (CW - 80), y + 4, { align: "right" });
      y += 9;
    });

    // ── SECTION 2 : COHÉRENCE ET LIGNE ARTISTIQUE ─────────────────────────────
    addPage();
    doc.setFillColor(249, 247, 244);
    doc.rect(0, 0, W, 297, "F");
    y = M;

    sf(9, "normal", PDF_COLORS.grey1);
    doc.text(T("2 / COHÉRENCE ET LIGNE ARTISTIQUE"), M, y + 6); y += 14;
    doc.setDrawColor(...PDF_COLORS.gold);
    doc.setLineWidth(0.5);
    doc.line(M, y, M + 30, y); y += 10;

    // Répartition par usage → cohérence identitaire
    const usageMap = {};
    const lieuMap  = {};
    const visiMap  = {};
    corpArtworks.forEach(aw => {
      const u = getCorpField(aw.id, "usage")      || "Non défini";
      const l = getCorpField(aw.id, "lieu_accrochage") || "Non défini";
      const v = getCorpField(aw.id, "visibilite") || "Non défini";
      usageMap[u] = (usageMap[u] || 0) + 1;
      lieuMap[l]  = (lieuMap[l]  || 0) + 1;
      visiMap[v]  = (visiMap[v]  || 0) + 1;
    });

    const patronMain = Object.entries(usageMap).sort(([, a], [, b]) => b - a)[0];
    const lieuPrincipal = Object.entries(lieuMap).sort(([, a], [, b]) => b - a)[0];

    sf(10, "bold", PDF_COLORS.black);
    doc.text(T("Analyse de cohérence"), M, y); y += 10;

    const nArtists = Object.keys(artistMap).length;
    const ratio = (corpArtworks.length / Math.max(nArtists, 1)).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const analyseLines = [
      patronMain
        ? T("L'usage dominant de la collection est \"{u}\" ({n} oeuvre(s)), ce qui indique une orientation {o}.", { u: T(patronMain[0]), n: patronMain[1], o: T(patronMain[0] === "Mécénat" ? "philanthropique et de soutien à la création" : patronMain[0] === "Patrimonial" ? "patrimoniale à long terme" : "décorative et d'ambiance professionnelle") })
        : T("Les usages de la collection ne sont pas encore renseignés."),
      lieuPrincipal
        ? T("Le lieu d'accrochage principal est \"{l}\" ({n} oeuvre(s)).", { l: T(lieuPrincipal[0]), n: lieuPrincipal[1] }) + " " + (/Hall|Réception/.test(lieuPrincipal[0]) ? T("Cette localisation favorise la visibilité clients et institutionnelle.") : T("Cette localisation soutient l'ambiance de travail et la culture interne."))
        : "",
      T("La collection compte {a} artiste(s) pour {n} oeuvre(s) — ratio de {r} oeuvre(s) par artiste en moyenne.", { a: nArtists, n: corpArtworks.length, r: ratio }),
      nArtists === 1 ? T("Collection mono-artiste : cohérence forte, mais vulnérabilité à l'évolution du marché de cet artiste.") : nArtists <= 5 ? T("Collection resserrée (< 5 artistes) : cohérence possible avec une ligne éditoriale claire.") : T("Collection diversifiée : richesse des approches, mais nécessite un fil conducteur curatorial explicite."),
    ].filter(Boolean);

    sf(9, "normal", PDF_COLORS.black);
    analyseLines.forEach(line => {
      checkY(16);
      doc.setFillColor(255, 255, 255);
      doc.rect(M, y, CW, 14, "F");
      doc.setFillColor(...PDF_COLORS.gold);
      doc.rect(M, y, 2, 14, "F");
      const wrapped = doc.splitTextToSize(line, CW - 8);
      wrapped.slice(0, 2).forEach((l, i) => { doc.text(l, M + 6, y + 5 + i * 5); });
      y += 18;
    });

    // ── SECTION 3 : RECOMMANDATIONS D'ACQUISITION ─────────────────────────────
    checkY(80);
    y += 6;
    sf(9, "normal", PDF_COLORS.grey1);
    doc.text(T("3 / RECOMMANDATIONS"), M, y + 6); y += 14;
    doc.setDrawColor(...PDF_COLORS.gold);
    doc.setLineWidth(0.5);
    doc.line(M, y, M + 30, y); y += 10;

    const isDecoPred = (usageMap["Décoratif"] || 0) > (corpArtworks.length / 2);
    const hasMecenat = (usageMap["Mécénat"] || 0) > 0;

    const acquisitionRecs = [
      { titre: T("Renforcer la ligne directrice"), detail: T("Définir un axe curatorial en 2-3 orientations (ex. : artistes émergents français, abstraction géométrique, photographie contemporaine) afin de donner de la cohérence à la collection et de faciliter les acquisitions futures.") },
      isDecoPred
        ? { titre: T("Équilibrer décoratif et patrimonial"), detail: T("La collection est majoritairement tournée vers la décoration. Il est conseillé d'intégrer progressivement des oeuvres à vocation patrimoniale — artistes reconnus, pièces documentées — pour renforcer la valeur à long terme.") }
        : { titre: T("Valoriser les oeuvres existantes"), detail: T("Avant toute nouvelle acquisition, il est recommandé de compléter la documentation des oeuvres actuelles : inventaire photographique, certificats d'authenticité, estimations à jour.") },
      hasMecenat
        ? { titre: T("Développer le mécénat culturel"), detail: T("La présence d'oeuvres liées au mécénat ouvre la possibilité de créer un programme structuré : partenariats avec des institutions, prix d'entreprise, résidences d'artistes — avec bénéfices fiscaux (loi Aillagon).") }
        : { titre: T("Explorer le mécénat comme levier"), detail: T("L'acquisition d'oeuvres via le mécénat permet à la fois de soutenir la création, de bénéficier d'avantages fiscaux (déduction à 60% de l'IS pour les entreprises) et de renforcer l'image institutionnelle.") },
      { titre: T("Politique d'accrochage et de rotation"), detail: T("Élaborer un calendrier de rotation des oeuvres entre les espaces (bureaux, salles de réunion, hall, réserve) pour maximiser la visibilité interne et renouveler l'expérience des collaborateurs et des clients.") },
    ];

    acquisitionRecs.forEach((r, i) => {
      checkY(28);
      doc.setFillColor(255, 255, 255);
      doc.rect(M, y, CW, 24, "F");
      doc.setFillColor(...PDF_COLORS.black);
      doc.rect(M, y, 2, 24, "F");
      sf(9, "bold", PDF_COLORS.black);
      doc.text(`${i + 1}. ${r.titre}`, M + 6, y + 7);
      sf(8, "normal", PDF_COLORS.grey1);
      const ls = doc.splitTextToSize(r.detail, CW - 12);
      ls.slice(0, 2).forEach((l, li) => { doc.text(l, M + 6, y + 13 + li * 5); });
      y += 28;
    });

    // ── NUMÉROTATION ─────────────────────────────────────────────────────────
    const nPg = doc.getNumberOfPages();
    for (let p = 1; p <= nPg; p++) {
      doc.setPage(p);
      if (p === 1) continue;
      sf(7, "normal", PDF_COLORS.grey1);
      doc.text(`${p} / ${nPg}`, W - M, 291, { align: "right" });
      doc.text(T("Rapport de cohérence curatoriale") + (corpClientName ? " — " + corpClientName : ""), M, 291);
      doc.setDrawColor(...PDF_COLORS.grey2);
      doc.setLineWidth(0.2);
      doc.line(M, 288, W - M, 288);
    }

    const slug = (corpClientName || T("collection")).replace(/[^a-zA-Z0-9]/g, "-").substring(0, 30);
    doc.save(`${T("rapport-curatorial-")}${slug}.pdf`);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // EXCEL CORPORATE
  // ══════════════════════════════════════════════════════════════════════════
  async function generateCorporateExcel() {
    const XLSX = await import("xlsx");
    DOC_LOCALE = locale;
    const wb = XLSX.utils.book_new();
    const colName = allCollections.find(c => c.id === selectedColId)?.title || T("Collection");
    const today = new Date().toLocaleDateString(locale);

    // Onglet 1 : Inventaire corporate complet
    const headers = [
      T("N° inventaire"), T("Titre"), T("Artiste"), T("Année"), T("Technique"), T("Dimensions"),
      T("Lieu d'accrochage"), T("Statut juridique"), T("Usage"), T("Visibilité"),
      T("État de conservation"), T("Valeur estimée (EUR)"),
    ];
    const rows = [
      [T("INVENTAIRE COLLECTION CORPORATE — {name}", { name: corpClientName || colName })],
      [corpAdvisorName ? T("Généré le {date} — Art Advisor : {name}", { date: today, name: corpAdvisorName }) : T("Généré le {date}", { date: today })],
      [],
      headers,
      ...corpArtworks.map(aw => [
        aw.inventory_number || "",
        aw.title || T("Sans titre"),
        aw.artist || "",
        aw.year || "",
        aw.medium || "",
        aw.dimensions || "",
        T(getCorpField(aw.id, "lieu_accrochage")),
        T(getCorpField(aw.id, "statut_juridique")),
        T(getCorpField(aw.id, "usage")),
        T(getCorpField(aw.id, "visibilite")),
        aw.condition || "",
        getCorpEstVal(aw) || "",
      ]),
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(rows);
    ws1["!cols"] = [
      { wch: 14 }, { wch: 30 }, { wch: 24 }, { wch: 6 }, { wch: 26 }, { wch: 16 },
      { wch: 20 }, { wch: 24 }, { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(wb, ws1, T("Inventaire corporate"));

    // Onglet 2 : Synthèse par usage
    const usageRows = [[T("Usage"), T("Nombre d'oeuvres"), T("Valeur totale estimée (EUR)")]];
    const usageSynth = {};
    corpArtworks.forEach(aw => {
      const u = getCorpField(aw.id, "usage") || "Non défini";
      if (!usageSynth[u]) usageSynth[u] = { count: 0, total: 0 };
      usageSynth[u].count++;
      usageSynth[u].total += getCorpEstVal(aw);
    });
    Object.entries(usageSynth).sort(([, a], [, b]) => b.count - a.count).forEach(([u, d]) => {
      usageRows.push([T(u), d.count, d.total || ""]);
    });
    const ws2 = XLSX.utils.aoa_to_sheet(usageRows);
    ws2["!cols"] = [{ wch: 26 }, { wch: 18 }, { wch: 24 }];
    XLSX.utils.book_append_sheet(wb, ws2, T("Synthèse par usage"));

    // Onglet 3 : Synthèse par lieu
    const lieuRows = [[T("Lieu d'accrochage"), T("Nombre d'oeuvres")]];
    const lieuSynth = {};
    corpArtworks.forEach(aw => {
      const l = getCorpField(aw.id, "lieu_accrochage") || "Non défini";
      lieuSynth[l] = (lieuSynth[l] || 0) + 1;
    });
    Object.entries(lieuSynth).sort(([, a], [, b]) => b - a).forEach(([l, c]) => lieuRows.push([T(l), c]));
    const ws3 = XLSX.utils.aoa_to_sheet(lieuRows);
    ws3["!cols"] = [{ wch: 26 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, ws3, T("Synthèse par lieu"));

    const slug = (corpClientName || T("collection")).replace(/[^a-zA-Z0-9]/g, "-").substring(0, 30);
    XLSX.writeFile(wb, `${T("collection-corporate-")}${slug}.xlsx`);
  }

  // ── Handler corporate ─────────────────────────────────────────────────────
  async function handleCorporateGenerate(format) {
    try {
      setCorpGenerating(true);
      setCorpError(null);
      setCorpSuccess(null);
      if (format === "audit" || format === "all") await generateCorporateAuditPDF();
      if (format === "curatorial" || format === "all") await generateCuratorialPDF();
      if (format === "excel" || format === "all") await generateCorporateExcel();
      setCorpSuccess(
        format === "all" ? T("Audit + Rapport curatorial + Excel générés avec succès.")
        : format === "audit" ? T("Rapport d'audit corporate généré.")
        : format === "curatorial" ? T("Rapport de cohérence curatoriale généré.")
        : T("Export Excel généré.")
      );
    } catch (e) {
      console.error("[CorporateReport] error:", e);
      setCorpError(T("Erreur lors de la génération : {m}", { m: e?.message || T("Erreur inconnue") }));
    } finally {
      setCorpGenerating(false);
    }
  }

  // ── Chargement données ─────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      setError(null);

      // Collections fondation
      const { data: cols, error: colErr } = await supabase
        .from("collections")
        .select("*")
        .eq("client_id", user.id)
        .in("collection_type", [
          "foundation_owned", "foundation_founder",
          "foundation_donation", "foundation_deposit",
        ]);
      if (colErr) throw colErr;

      const colList = cols || [];
      setCollections(colList);

      // Prénommer depuis le profil
      if (profile?.first_name || profile?.last_name) {
        setDirectorName(`${profile.first_name || ""} ${profile.last_name || ""}`.trim());
      }
      if (profile?.company_name || profile?.company) {
        setFoundationName(profile.company_name || profile.company || "");
      }

      // Œuvres par collection
      const artworksMap = {};
      await Promise.all(colList.map(async (col) => {
        const { data: aws } = await supabase
          .from("artworks_secure")
          .select([
            "id", "title", "artist", "year", "medium", "dimensions",
            "inventory_number", "condition", "estimated_value",
            "current_estimated_value", "valuation_consensus",
            "valuation_market_value", "valuation_low", "valuation_high",
            "artwork_status", "loan_status",
          ].join(","))
          .eq("collection_id", col.id);
        artworksMap[col.id] = aws || [];
      }));
      setArtworksByCol(artworksMap);

      // Tiers par collection
      const tpMap = {};
      const colIds = colList.map(c => c.id);
      if (colIds.length > 0) {
        const { data: tps } = await supabase
          .from("collection_third_parties")
          .select("*")
          .in("collection_id", colIds);
        (tps || []).forEach(tp => { tpMap[tp.collection_id] = tp; });
      }
      setThirdParties(tpMap);

      // Rapports passés
      const { data: reports } = await supabase
        .from("foundation_annual_reports")
        .select("id, year, status, generated_at, total_works, total_value_eur, report_url")
        .eq("client_id", user.id)
        .order("year", { ascending: false })
        .limit(5);
      setPastReports(reports || []);

    } catch (e) {
      console.error("[FoundationReport] loadData error:", e);
      setError(T("Erreur lors du chargement des données."));
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, profile, T]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Upload logo ────────────────────────────────────────────────────────────
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setFoundationLogo(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ── Calculs snapshot ──────────────────────────────────────────────────────
  function buildSnapshot() {
    const getEstVal = (aw) =>
      Number(aw.current_estimated_value || aw.valuation_consensus ||
             aw.valuation_market_value  || aw.estimated_value || 0);

    const collectionsSnap = collections.map(col => {
      const aws = artworksByCol[col.id] || [];
      const tp  = thirdParties[col.id] || null;
      const totalValue = col.collection_type === "foundation_owned"
        ? aws.reduce((s, a) => s + getEstVal(a), 0)
        : 0; // non valorisé pour les autres types
      return {
        id:              col.id,
        title:           col.title,
        collection_type: col.collection_type,
        legal_status:    col.legal_status,
        artworkCount:    aws.length,
        totalValue,
        thirdParty:      tp ? { party_name: tp.party_name, end_date: tp.end_date, contract_ref: tp.contract_ref } : null,
        artworks: aws.map(a => ({
          id:               a.id,
          inventory_number: a.inventory_number,
          title:            a.title,
          artist:           a.artist,
          year:             a.year,
          medium:           a.medium,
          dimensions:       a.dimensions,
          condition:        a.condition,
          estimated_value:  getEstVal(a),
          artwork_status:   a.artwork_status,
          loan_status:      a.loan_status,
        })),
      };
    });

    const totalWorks = collectionsSnap.reduce((s, c) => s + c.artworkCount, 0);
    const totalValue = collectionsSnap.reduce((s, c) => s + c.totalValue, 0);

    return { collections: collectionsSnap, totalWorks, totalValue };
  }

  // ── Sauvegarde en base ────────────────────────────────────────────────────
  async function saveReportRecord(snapshot, reportUrl = null, status = "final") {
    const payload = {
      client_id:       user.id,
      year:            reportYear,
      generated_by:    user.id,
      total_works:     snapshot.totalWorks,
      total_value_eur: snapshot.totalValue,
      snapshot_json:   snapshot,
      collection_ids:  collections.map(c => c.id),
      report_url:      reportUrl,
      status,
      director_note:   directorNote || null,
    };

    // Vérifier si un brouillon existe déjà pour cette année
    const { data: existing } = await supabase
      .from("foundation_annual_reports")
      .select("id, status")
      .eq("client_id", user.id)
      .eq("year", reportYear)
      .maybeSingle();

    if (existing?.id) {
      if (existing.status === "final" || existing.status === "submitted_to_board") {
        // Ne pas écraser un rapport finalisé
        return existing.id;
      }
      await supabase
        .from("foundation_annual_reports")
        .update(payload)
        .eq("id", existing.id);
      return existing.id;
    } else {
      const { data } = await supabase
        .from("foundation_annual_reports")
        .insert([payload])
        .select("id")
        .single();
      return data?.id;
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // EXPORT PDF (jsPDF)
  // ════════════════════════════════════════════════════════════════════════════
  async function generatePDF() {
    const { default: jsPDF } = await import("jspdf");
    const snapshot = buildSnapshot();
    DOC_LOCALE = locale;
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    const W = 210;
    const MARGIN = 18;
    const CONTENT_W = W - MARGIN * 2;
    let y = 0;

    const addPage = () => { doc.addPage(); y = MARGIN; };
    const checkY = (needed = 20) => { if (y + needed > 280) addPage(); };

    const setFont = (size, style = "normal", color = PDF_COLORS.black) => {
      doc.setFontSize(size);
      doc.setFont("helvetica", style);
      doc.setTextColor(...color);
    };

    // ── PAGE DE GARDE ────────────────────────────────────────────────────────
    // Fond noir
    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(0, 0, W, 297, "F");

    // Logo fondation (si fourni)
    if (foundationLogo) {
      try {
        doc.addImage(foundationLogo, "PNG", MARGIN, 24, 32, 32, "", "FAST");
      } catch { /* ignore */ }
    }

    // Bande dorée
    doc.setFillColor(...PDF_COLORS.gold);
    doc.rect(MARGIN, 70, 2, 60, "F");

    // Titre
    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.setTextColor(255, 255, 255);
    doc.text(T("RAPPORT ANNUEL"), MARGIN + 8, 88);
    doc.text(T("CONSEIL D'ADMINISTRATION"), MARGIN + 8, 100);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(16);
    doc.setTextColor(...PDF_COLORS.gold);
    doc.text(T("Exercice {year}", { year: reportYear }), MARGIN + 8, 116);

    // Nom fondation
    if (foundationName) {
      doc.setFontSize(13);
      doc.setTextColor(180, 180, 180);
      doc.text(foundationName, MARGIN + 8, 130);
    }

    // Ligne séparatrice
    doc.setDrawColor(...PDF_COLORS.gold);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, 210, W - MARGIN, 210);

    // Infos bas de page
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(T("Généré le {date}", { date: fmtDateLong(new Date()) }), MARGIN, 220);
    if (directorName) {
      doc.text(T("Directeur : {name}", { name: directorName }), MARGIN, 228);
    }
    doc.text(T("Document confidentiel — Usage interne Conseil d'Administration"), MARGIN, 260);

    // ── SOMMAIRE ─────────────────────────────────────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");

    setFont(9, "normal", PDF_COLORS.grey1);
    doc.text(T("SOMMAIRE"), MARGIN, y + 6);
    y += 14;

    setFont(20, "normal", PDF_COLORS.black);
    doc.setFont("helvetica", "bold");
    doc.text(T("Table des matières"), MARGIN, y);
    y += 10;

    doc.setDrawColor(...PDF_COLORS.gold);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, y, MARGIN + 30, y);
    y += 12;

    const tocItems = [
      T("1. Synthèse patrimoniale"),
      T("2. Inventaire par collection"),
      T("3. État de conservation"),
      T("4. Conventions et tiers"),
      T("5. Note du Directeur"),
      T("6. Certification"),
    ];
    tocItems.forEach((item, i) => {
      setFont(12, "normal", PDF_COLORS.black);
      doc.text(item, MARGIN + 4, y);
      setFont(12, "normal", PDF_COLORS.grey1);
      doc.text(`${i + 3}`, W - MARGIN - 4, y, { align: "right" });
      y += 9;
    });

    // ── SECTION 1 : SYNTHÈSE PATRIMONIALE ────────────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = MARGIN;

    // En-tête section
    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(MARGIN, y, CONTENT_W, 12, "F");
    setFont(10, "bold", PDF_COLORS.white);
    doc.text(T("1. SYNTHÈSE PATRIMONIALE"), MARGIN + 4, y + 8);
    y += 18;

    // Cartes stats (2 colonnes)
    const cardW = (CONTENT_W - 6) / 2;
    const cards = [
      { label: T("TOTAL OEUVRES"), value: String(snapshot.totalWorks), sub: T("{n} collection(s)", { n: collections.length }) },
      { label: T("VALEUR PROPRIETE PROPRE"), value: fmtEur(snapshot.totalValue), sub: T("Hors prets et depots") },
    ];
    cards.forEach((card, i) => {
      const cx = MARGIN + i * (cardW + 6);
      doc.setFillColor(255, 255, 255);
      doc.rect(cx, y, cardW, 28, "F");
      doc.setDrawColor(...PDF_COLORS.grey2);
      doc.setLineWidth(0.3);
      doc.rect(cx, y, cardW, 28, "S");
      // Barre top couleur
      doc.setFillColor(...PDF_COLORS.gold);
      doc.rect(cx, y, cardW, 1.5, "F");
      setFont(7, "normal", PDF_COLORS.grey1);
      doc.text(card.label, cx + 4, y + 8);
      setFont(14, "bold", PDF_COLORS.black);
      doc.text(card.value, cx + 4, y + 18);
      setFont(8, "normal", PDF_COLORS.grey1);
      doc.text(card.sub, cx + 4, y + 24);
    });
    y += 36;

    // Tableau récapitulatif par collection
    setFont(11, "bold", PDF_COLORS.black);
    doc.text(T("Répartition par collection"), MARGIN, y);
    y += 8;

    // En-tête tableau
    const colWidths = [60, 30, 22, 42, 20];
    const headers = [T("Collection"), T("Type"), T("Oeuvres"), T("Valeur estimée"), T("Statut")];
    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(MARGIN, y, CONTENT_W, 7, "F");
    let xPos = MARGIN + 2;
    headers.forEach((h, i) => {
      setFont(7, "bold", PDF_COLORS.white);
      doc.text(h, xPos, y + 5);
      xPos += colWidths[i];
    });
    y += 7;

    snapshot.collections.forEach((col, idx) => {
      checkY(8);
      const bg = idx % 2 === 0 ? [255, 255, 255] : [249, 247, 244];
      doc.setFillColor(...bg);
      doc.rect(MARGIN, y, CONTENT_W, 7, "F");
      xPos = MARGIN + 2;
      const rowData = [
        col.title.substring(0, 28),
        (COLLECTION_TYPE_LABELS[col.collection_type] ? T(COLLECTION_TYPE_LABELS[col.collection_type]) : col.collection_type).substring(0, 18),
        String(col.artworkCount),
        col.totalValue > 0 ? fmtEur(col.totalValue) : T("Non valorise"),
        col.thirdParty ? T("Avec tiers") : "—",
      ];
      rowData.forEach((val, i) => {
        setFont(7, "normal", PDF_COLORS.black);
        doc.text(val, xPos, y + 5);
        xPos += colWidths[i];
      });
      y += 7;
    });
    y += 6;

    // ── SECTION 2 : INVENTAIRE PAR COLLECTION ────────────────────────────────
    snapshot.collections.forEach((col) => {
      addPage();
      doc.setFillColor(...PDF_COLORS.cream);
      doc.rect(0, 0, W, 297, "F");
      y = MARGIN;

      // En-tête collection
      const typeColor = COLLECTION_TYPE_COLORS_PDF[col.collection_type] || PDF_COLORS.black;
      doc.setFillColor(...typeColor);
      doc.rect(MARGIN, y, 3, 14, "F");
      doc.setFillColor(255, 255, 255);
      doc.rect(MARGIN + 3, y, CONTENT_W - 3, 14, "F");
      doc.setDrawColor(...PDF_COLORS.grey2);
      doc.setLineWidth(0.2);
      doc.rect(MARGIN, y, CONTENT_W, 14, "S");

      setFont(7, "normal", PDF_COLORS.grey1);
      doc.text(T("2. INVENTAIRE — {type}", { type: T(COLLECTION_TYPE_LABELS[col.collection_type] || "").toUpperCase() }), MARGIN + 6, y + 5);
      setFont(13, "bold", PDF_COLORS.black);
      doc.text(col.title, MARGIN + 6, y + 12);
      setFont(8, "normal", PDF_COLORS.grey1);
      doc.text(T("{n} oeuvre(s)", { n: col.artworkCount }), W - MARGIN - 2, y + 12, { align: "right" });
      y += 20;

      // Tiers si présent
      if (col.thirdParty) {
        doc.setFillColor(240, 244, 248);
        doc.rect(MARGIN, y, CONTENT_W, 12, "F");
        setFont(8, "normal", PDF_COLORS.grey1);
        doc.text(T("Proprietaire / preteur : {name}", { name: col.thirdParty.party_name || "—" }), MARGIN + 4, y + 5);
        if (col.thirdParty.contract_ref) {
          doc.text(T("Convention : {ref}", { ref: col.thirdParty.contract_ref }), MARGIN + 4, y + 10);
        }
        if (col.thirdParty.end_date) {
          doc.text(T("Echeance : {date}", { date: fmtDate(col.thirdParty.end_date) }), W - MARGIN - 2, y + 5, { align: "right" });
        }
        y += 16;
      }

      // Tableau œuvres
      const awColW = [8, 36, 30, 10, 40, 22, 16, 12];
      const awHeaders = [T("N°"), T("Titre"), T("Artiste"), T("An."), T("Technique"), T("Dimensions"), T("Valeur"), T("Etat")];
      doc.setFillColor(...PDF_COLORS.black);
      doc.rect(MARGIN, y, CONTENT_W, 6, "F");
      xPos = MARGIN + 1;
      awHeaders.forEach((h, i) => {
        setFont(6, "bold", PDF_COLORS.white);
        doc.text(h, xPos, y + 4.5);
        xPos += awColW[i];
      });
      y += 6;

      col.artworks.forEach((aw, idx) => {
        checkY(7);
        const bg = idx % 2 === 0 ? [255, 255, 255] : [249, 247, 244];
        doc.setFillColor(...bg);
        doc.rect(MARGIN, y, CONTENT_W, 6, "F");
        xPos = MARGIN + 1;
        const getEstValLocal = () => {
          if (aw.estimated_value > 0) return fmtEur(aw.estimated_value);
          return col.collection_type !== "foundation_owned" ? T("Non val.") : "—";
        };
        const rowData = [
          (aw.inventory_number || "—").substring(0, 6),
          (aw.title || T("Sans titre")).substring(0, 18),
          (aw.artist || "—").substring(0, 16),
          String(aw.year || "—").substring(0, 4),
          (aw.medium || "—").substring(0, 22),
          (aw.dimensions || "—").substring(0, 12),
          getEstValLocal().substring(0, 14),
          (aw.condition || "—").substring(0, 6),
        ];
        rowData.forEach((val, i) => {
          setFont(6, "normal", PDF_COLORS.black);
          doc.text(val, xPos, y + 4.5);
          xPos += awColW[i];
        });
        y += 6;
      });
    });

    // ── SECTION 3 : ÉTAT DE CONSERVATION ─────────────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = MARGIN;

    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(MARGIN, y, CONTENT_W, 12, "F");
    setFont(10, "bold", PDF_COLORS.white);
    doc.text(T("3. ÉTAT DE CONSERVATION"), MARGIN + 4, y + 8);
    y += 18;

    // Comptage états
    const conditionCounts = { bon: 0, satisfaisant: 0, attention: 0, nonRenseigne: 0 };
    snapshot.collections.forEach(col => {
      col.artworks.forEach(aw => {
        const c = (aw.condition || "").toLowerCase();
        if (c === "good" || c === "bon" || c === "très bon") conditionCounts.bon++;
        else if (c === "fair" || c === "satisfaisant") conditionCounts.satisfaisant++;
        else if (c === "poor" || c === "attention" || c === "mauvais") conditionCounts.attention++;
        else conditionCounts.nonRenseigne++;
      });
    });

    const total = snapshot.totalWorks || 1;
    const conservItems = [
      { label: T("Bon état"), count: conditionCounts.bon, color: [44, 122, 75] },
      { label: T("État satisfaisant"), count: conditionCounts.satisfaisant, color: [201, 169, 97] },
      { label: T("Attention requise"), count: conditionCounts.attention, color: [184, 64, 64] },
      { label: T("Non renseigné"), count: conditionCounts.nonRenseigne, color: [150, 150, 150] },
    ];

    conservItems.forEach(item => {
      checkY(14);
      const pct = Math.round((item.count / total) * 100);
      const barW = Math.max(1, Math.round((item.count / total) * (CONTENT_W - 60)));

      setFont(9, "normal", PDF_COLORS.black);
      doc.text(item.label, MARGIN, y + 5);
      setFont(9, "bold", PDF_COLORS.black);
      doc.text(`${item.count}`, MARGIN + 60, y + 5);
      setFont(8, "normal", PDF_COLORS.grey1);
      doc.text(`${pct}%`, MARGIN + 72, y + 5);

      doc.setFillColor(...PDF_COLORS.grey2);
      doc.rect(MARGIN + 84, y, CONTENT_W - 84, 5, "F");
      if (item.count > 0) {
        doc.setFillColor(...item.color);
        doc.rect(MARGIN + 84, y, barW, 5, "F");
      }
      y += 12;
    });

    // ── SECTION 4 : CONVENTIONS ET TIERS ─────────────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = MARGIN;

    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(MARGIN, y, CONTENT_W, 12, "F");
    setFont(10, "bold", PDF_COLORS.white);
    doc.text(T("4. CONVENTIONS ET TIERS"), MARGIN + 4, y + 8);
    y += 18;

    const tpCols = snapshot.collections.filter(c => c.thirdParty);
    if (tpCols.length === 0) {
      setFont(10, "normal", PDF_COLORS.grey1);
      doc.text(T("Aucune convention avec tiers enregistrée."), MARGIN, y);
      y += 10;
    } else {
      tpCols.forEach(col => {
        checkY(30);
        const tp = col.thirdParty;
        doc.setFillColor(255, 255, 255);
        doc.rect(MARGIN, y, CONTENT_W, 26, "F");
        doc.setDrawColor(...PDF_COLORS.grey2);
        doc.setLineWidth(0.2);
        doc.rect(MARGIN, y, CONTENT_W, 26, "S");

        const typeColor = COLLECTION_TYPE_COLORS_PDF[col.collection_type] || PDF_COLORS.black;
        doc.setFillColor(...typeColor);
        doc.rect(MARGIN, y, 2, 26, "F");

        setFont(9, "bold", PDF_COLORS.black);
        doc.text(col.title, MARGIN + 5, y + 7);
        setFont(8, "normal", PDF_COLORS.grey1);
        doc.text(T(COLLECTION_TYPE_LABELS[col.collection_type] || ""), MARGIN + 5, y + 13);
        doc.text(T("Tiers : {name}", { name: tp.party_name || "—" }), MARGIN + 5, y + 19);
        if (tp.contract_ref) doc.text(T("Convention : {ref}", { ref: tp.contract_ref }), MARGIN + 80, y + 13);
        if (tp.end_date) doc.text(T("Echeance : {date}", { date: fmtDate(tp.end_date) }), MARGIN + 80, y + 19);
        y += 30;
      });
    }

    // ── SECTION 5 : NOTE DU DIRECTEUR ────────────────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = MARGIN;

    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(MARGIN, y, CONTENT_W, 12, "F");
    setFont(10, "bold", PDF_COLORS.white);
    doc.text(T("5. NOTE DU DIRECTEUR"), MARGIN + 4, y + 8);
    y += 18;

    if (directorNote) {
      const lines = doc.splitTextToSize(directorNote, CONTENT_W);
      setFont(10, "normal", PDF_COLORS.black);
      lines.forEach(line => {
        checkY(7);
        doc.text(line, MARGIN, y);
        y += 6;
      });
    } else {
      setFont(10, "normal", PDF_COLORS.grey1);
      doc.text(T("Aucune note redigee."), MARGIN, y);
      y += 10;
    }

    // ── SECTION 6 : CERTIFICATION ────────────────────────────────────────────
    addPage();
    doc.setFillColor(...PDF_COLORS.cream);
    doc.rect(0, 0, W, 297, "F");
    y = MARGIN;

    doc.setFillColor(...PDF_COLORS.black);
    doc.rect(MARGIN, y, CONTENT_W, 12, "F");
    setFont(10, "bold", PDF_COLORS.white);
    doc.text(T("6. CERTIFICATION"), MARGIN + 4, y + 8);
    y += 22;

    setFont(10, "normal", PDF_COLORS.black);
    const certText = foundationName
      ? T("Je soussigné(e), Directeur(rice) de {name}, certifie que les informations contenues dans le présent rapport annuel pour l'exercice {year} sont exactes et conformes aux données enregistrées dans le système de gestion de collection Artkiv.", { name: foundationName, year: reportYear })
      : T("Je soussigné(e), Directeur(rice) de la fondation, certifie que les informations contenues dans le présent rapport annuel pour l'exercice {year} sont exactes et conformes aux données enregistrées dans le système de gestion de collection Artkiv.", { year: reportYear });
    const certLines = doc.splitTextToSize(certText, CONTENT_W);
    certLines.forEach(line => { doc.text(line, MARGIN, y); y += 6; });
    y += 14;

    // Bloc signature
    doc.setDrawColor(...PDF_COLORS.grey2);
    doc.setLineWidth(0.3);
    doc.rect(MARGIN, y, 80, 28, "S");
    setFont(8, "normal", PDF_COLORS.grey1);
    doc.text(T("Signature du Directeur"), MARGIN + 4, y + 6);
    if (directorName) {
      setFont(9, "bold", PDF_COLORS.black);
      doc.text(directorName, MARGIN + 4, y + 22);
    }

    doc.rect(MARGIN + 90, y, 80, 28, "S");
    setFont(8, "normal", PDF_COLORS.grey1);
    doc.text(T("Date de soumission au CA"), MARGIN + 94, y + 6);
    setFont(9, "bold", PDF_COLORS.black);
    doc.text(fmtDateLong(new Date()), MARGIN + 94, y + 22);
    y += 36;

    // Logo fondation en bas si fourni
    if (foundationLogo) {
      try {
        doc.addImage(foundationLogo, "PNG", MARGIN, y, 20, 20, "", "FAST");
      } catch { /* ignore */ }
    }
    if (foundationName) {
      setFont(9, "normal", PDF_COLORS.gold);
      doc.text(foundationName, foundationLogo ? MARGIN + 24 : MARGIN, y + 12);
    }

    // ── Numérotation pages ────────────────────────────────────────────────────
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      if (p === 1) continue; // pas de numéro sur la couverture
      setFont(8, "normal", PDF_COLORS.grey1);
      doc.text(`${p} / ${totalPages}`, W - MARGIN, 291, { align: "right" });
      doc.text(T("Rapport CA {year} — {name}", { year: reportYear, name: foundationName || T("Fondation") }), MARGIN, 291);
      doc.setDrawColor(...PDF_COLORS.grey2);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, 288, W - MARGIN, 288);
    }

    doc.save(`${T("rapport-CA-")}${reportYear}-${(foundationName || T("fondation")).replace(/\s+/g, "-")}.pdf`);
    return snapshot;
  }

  // ════════════════════════════════════════════════════════════════════════════
  // EXPORT WORD (docx-js)
  // ════════════════════════════════════════════════════════════════════════════
  async function generateWord() {
    const {
      Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
      AlignmentType, HeadingLevel, BorderStyle, WidthType, ShadingType,
      VerticalAlign, PageBreak,
    } = await import("docx");

    const snapshot = buildSnapshot();
    DOC_LOCALE = locale;
    const border = { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" };
    const borders = { top: border, bottom: border, left: border, right: border };
    const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
    const noBorders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };

    const cell = (text, opts = {}) => new TableCell({
      borders: opts.noBorder ? noBorders : borders,
      width: opts.width ? { size: opts.width, type: WidthType.DXA } : undefined,
      shading: opts.shade ? { fill: opts.shade, type: ShadingType.CLEAR } : undefined,
      margins: { top: 80, bottom: 80, left: 120, right: 120 },
      verticalAlign: VerticalAlign.CENTER,
      children: [new Paragraph({
        alignment: opts.align || AlignmentType.LEFT,
        children: [new TextRun({
          text: String(text ?? "—"),
          size: opts.size || 18,
          bold: opts.bold || false,
          color: opts.color || "1A1A1A",
          font: "Arial",
        })],
      })],
    });

    const heading = (text, level = HeadingLevel.HEADING_1) =>
      new Paragraph({ heading: level, children: [new TextRun({ text, font: "Arial", bold: true })] });

    const para = (text, opts = {}) => new Paragraph({
      alignment: opts.align || AlignmentType.LEFT,
      spacing: { before: opts.spaceBefore || 80, after: opts.spaceAfter || 80 },
      children: [new TextRun({
        text: String(text ?? ""),
        size: opts.size || 20,
        bold: opts.bold || false,
        color: opts.color || "1A1A1A",
        font: "Arial",
      })],
    });

    const pageBreak = () => new Paragraph({ children: [new PageBreak()] });

    const children = [
      // ── Page de garde ──
      para(""),
      para(T("RAPPORT ANNUEL"), { bold: true, size: 40, color: "1A1A1A", align: AlignmentType.CENTER }),
      para(T("CONSEIL D'ADMINISTRATION"), { bold: true, size: 28, color: "C9A961", align: AlignmentType.CENTER }),
      para(T("Exercice {year}", { year: reportYear }), { size: 24, color: "1A1A1A", align: AlignmentType.CENTER, spaceBefore: 200 }),
      para(foundationName || "", { size: 20, color: "6B6B6B", align: AlignmentType.CENTER }),
      para(T("Généré le {date}", { date: fmtDateLong(new Date()) }), { size: 16, color: "9C9C9C", align: AlignmentType.CENTER, spaceBefore: 400 }),
      para(directorName ? T("Directeur : {name}", { name: directorName }) : "", { size: 16, color: "9C9C9C", align: AlignmentType.CENTER }),
      pageBreak(),

      // ── Synthèse patrimoniale ──
      heading(T("1. Synthèse patrimoniale")),
      para(T("Total oeuvres : {n}", { n: snapshot.totalWorks }), { bold: true }),
      para(T("Valeur propriété propre : {v}", { v: fmtEur(snapshot.totalValue) }), { bold: true }),
      para(""),

      // Tableau collections
      new Table({
        width: { size: 9026, type: WidthType.DXA },
        columnWidths: [2706, 1806, 1206, 2108, 1200],
        rows: [
          new TableRow({
            tableHeader: true,
            children: [
              cell(T("Collection"), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 2706 }),
              cell(T("Type"), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 1806 }),
              cell(T("Oeuvres"), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 1206, align: AlignmentType.CENTER }),
              cell(T("Valeur estimée"), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 2108, align: AlignmentType.RIGHT }),
              cell(T("Tiers"), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 1200, align: AlignmentType.CENTER }),
            ],
          }),
          ...snapshot.collections.map((col, idx) => new TableRow({
            children: [
              cell(col.title, { width: 2706, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4" }),
              cell((COLLECTION_TYPE_LABELS[col.collection_type] ? T(COLLECTION_TYPE_LABELS[col.collection_type]) : col.collection_type), { width: 1806, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4" }),
              cell(String(col.artworkCount), { width: 1206, align: AlignmentType.CENTER, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4" }),
              cell(col.totalValue > 0 ? fmtEur(col.totalValue) : T("Non valorisé"), { width: 2108, align: AlignmentType.RIGHT, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4" }),
              cell(col.thirdParty ? col.thirdParty.party_name || T("Oui") : "—", { width: 1200, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4" }),
            ],
          })),
        ],
      }),
      pageBreak(),

      // ── Inventaire par collection ──
      heading(T("2. Inventaire par collection")),
      ...snapshot.collections.flatMap(col => [
        para(""),
        heading(col.title, HeadingLevel.HEADING_2),
        para(T("Type : {type} — {n} oeuvre(s)", { type: (COLLECTION_TYPE_LABELS[col.collection_type] ? T(COLLECTION_TYPE_LABELS[col.collection_type]) : col.collection_type), n: col.artworkCount }), { color: "6B6B6B" }),
        col.thirdParty ? para(T("Tiers : {name}", { name: col.thirdParty.party_name || "—" }) + (col.thirdParty.contract_ref ? " | " + T("Conv. : {ref}", { ref: col.thirdParty.contract_ref }) : ""), { color: "6B6B6B" }) : para(""),
        para(""),
        new Table({
          width: { size: 9026, type: WidthType.DXA },
          columnWidths: [700, 1900, 1600, 600, 2000, 1126, 1100],
          rows: [
            new TableRow({
              tableHeader: true,
              children: [
                cell(T("N° inv."), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 700 }),
                cell(T("Titre"), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 1900 }),
                cell(T("Artiste"), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 1600 }),
                cell(T("An."), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 600, align: AlignmentType.CENTER }),
                cell(T("Technique"), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 2000 }),
                cell(T("Valeur est."), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 1126, align: AlignmentType.RIGHT }),
                cell(T("Etat"), { bold: true, shade: "1A1A1A", color: "FFFFFF", width: 1100 }),
              ],
            }),
            ...col.artworks.map((aw, idx) => new TableRow({
              children: [
                cell(aw.inventory_number || "—", { width: 700, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4", size: 16 }),
                cell(aw.title || T("Sans titre"), { width: 1900, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4", size: 16 }),
                cell(aw.artist || "—", { width: 1600, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4", size: 16 }),
                cell(String(aw.year || "—"), { width: 600, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4", size: 16, align: AlignmentType.CENTER }),
                cell(aw.medium || "—", { width: 2000, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4", size: 16 }),
                cell(aw.estimated_value > 0 ? fmtEur(aw.estimated_value) : "—", { width: 1126, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4", size: 16, align: AlignmentType.RIGHT }),
                cell(aw.condition || "—", { width: 1100, shade: idx % 2 === 0 ? "FFFFFF" : "F9F7F4", size: 16 }),
              ],
            })),
          ],
        }),
      ]),
      pageBreak(),

      // ── Note du directeur ──
      heading(T("5. Note du Directeur")),
      para(directorNote || T("Aucune note rédigée.")),
      pageBreak(),

      // ── Certification ──
      heading(T("6. Certification")),
      para(foundationName
        ? T("Je soussigné(e), Directeur(rice) de {name}, certifie que les informations contenues dans le présent rapport annuel pour l'exercice {year} sont exactes et conformes aux données enregistrées dans le système de gestion de collection Artkiv.", { name: foundationName, year: reportYear })
        : T("Je soussigné(e), Directeur(rice) de la fondation, certifie que les informations contenues dans le présent rapport annuel pour l'exercice {year} sont exactes et conformes aux données enregistrées dans le système de gestion de collection Artkiv.", { year: reportYear })),
      para(""),
      para(""),
      new Table({
        width: { size: 9026, type: WidthType.DXA },
        columnWidths: [4513, 4513],
        rows: [
          new TableRow({
            children: [
              cell(T("Signature du Directeur"), { noBorder: true, width: 4513, bold: true }),
              cell(T("Date de soumission au CA"), { noBorder: true, width: 4513, bold: true }),
            ],
          }),
          new TableRow({
            children: [
              cell(directorName || "...........................", { noBorder: true, width: 4513 }),
              cell(fmtDateLong(new Date()), { noBorder: true, width: 4513 }),
            ],
          }),
        ],
      }),
    ];

    const doc = new Document({
      styles: {
        default: { document: { run: { font: "Arial", size: 20 } } },
        paragraphStyles: [
          { id: "Heading1", name: "Heading 1", basedOn: "Normal", next: "Normal", quickFormat: true,
            run: { size: 32, bold: true, font: "Arial", color: "1A1A1A" },
            paragraph: { spacing: { before: 360, after: 180 }, outlineLevel: 0 } },
          { id: "Heading2", name: "Heading 2", basedOn: "Normal", next: "Normal", quickFormat: true,
            run: { size: 26, bold: true, font: "Arial", color: "1A1A1A" },
            paragraph: { spacing: { before: 280, after: 140 }, outlineLevel: 1 } },
        ],
      },
      sections: [{
        properties: {
          page: {
            size: { width: 11906, height: 16838 },
            margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
          },
        },
        children,
      }],
    });

    const buffer = await Packer.toBuffer(doc);
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${T("rapport-CA-")}${reportYear}-${(foundationName || T("fondation")).replace(/\s+/g, "-")}.docx`;
    a.click();
    URL.revokeObjectURL(url);
    return snapshot;
  }

  // ════════════════════════════════════════════════════════════════════════════
  // EXPORT EXCEL (SheetJS / xlsx)
  // ════════════════════════════════════════════════════════════════════════════
  async function generateExcel() {
    const XLSX = await import("xlsx");
    const snapshot = buildSnapshot();
    DOC_LOCALE = locale;
    const wb = XLSX.utils.book_new();

    // ── Onglet 1 : Synthèse ──────────────────────────────────────────────────
    const synthData = [
      [T("RAPPORT ANNUEL CA — EXERCICE {year}", { year: reportYear })],
      [foundationName || ""],
      [T("Généré le {date}", { date: fmtDateLong(new Date()) })],
      [],
      [T("Collection"), T("Type"), T("Oeuvres"), T("Valeur estimée (EUR)"), T("Tiers")],
      ...snapshot.collections.map(col => [
        col.title,
        (COLLECTION_TYPE_LABELS[col.collection_type] ? T(COLLECTION_TYPE_LABELS[col.collection_type]) : col.collection_type),
        col.artworkCount,
        col.totalValue > 0 ? col.totalValue : T("Non valorisé"),
        col.thirdParty ? col.thirdParty.party_name || T("Oui") : "—",
      ]),
      [],
      [T("TOTAL"), "", snapshot.totalWorks, snapshot.totalValue, ""],
    ];
    const wsSynth = XLSX.utils.aoa_to_sheet(synthData);
    wsSynth["!cols"] = [{ wch: 35 }, { wch: 22 }, { wch: 10 }, { wch: 22 }, { wch: 25 }];
    XLSX.utils.book_append_sheet(wb, wsSynth, T("Synthèse"));

    // ── Onglet 2 : Inventaire complet ────────────────────────────────────────
    const invHeaders = [
      T("Collection"), T("Type collection"), T("N° inventaire"), T("Titre"),
      T("Artiste"), T("Année"), T("Technique"), T("Dimensions"), T("Condition"),
      T("Valeur estimée (EUR)"), T("Statut oeuvre"),
    ];
    const invRows = [invHeaders];
    snapshot.collections.forEach(col => {
      col.artworks.forEach(aw => {
        invRows.push([
          col.title,
          (COLLECTION_TYPE_LABELS[col.collection_type] ? T(COLLECTION_TYPE_LABELS[col.collection_type]) : col.collection_type),
          aw.inventory_number || "",
          aw.title || T("Sans titre"),
          aw.artist || "",
          aw.year || "",
          aw.medium || "",
          aw.dimensions || "",
          aw.condition || "",
          aw.estimated_value > 0 ? aw.estimated_value : "",
          aw.artwork_status || "",
        ]);
      });
    });
    const wsInv = XLSX.utils.aoa_to_sheet(invRows);
    wsInv["!cols"] = [
      { wch: 28 }, { wch: 20 }, { wch: 14 }, { wch: 30 },
      { wch: 24 }, { wch: 8 }, { wch: 28 }, { wch: 18 },
      { wch: 14 }, { wch: 18 }, { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(wb, wsInv, T("Inventaire complet"));

    // ── Onglet 3 : Conventions tiers ─────────────────────────────────────────
    const tpHeaders = [
      T("Collection"), T("Type collection"), T("Tiers"), T("Type tiers"),
      T("Référence convention"), T("Début"), T("Fin"), T("Assuré par"),
    ];
    const tpRows = [tpHeaders];
    snapshot.collections.forEach(col => {
      if (col.thirdParty) {
        const tp = thirdParties[col.id] || {};
        tpRows.push([
          col.title,
          (COLLECTION_TYPE_LABELS[col.collection_type] ? T(COLLECTION_TYPE_LABELS[col.collection_type]) : col.collection_type),
          col.thirdParty.party_name || "—",
          tp.party_type || "—",
          col.thirdParty.contract_ref || "—",
          fmtDate(tp.start_date),
          col.thirdParty.end_date ? fmtDate(col.thirdParty.end_date) : T("Indéterminée"),
          tp.insured_by || "—",
        ]);
      }
    });
    const wsTp = XLSX.utils.aoa_to_sheet(tpRows);
    wsTp["!cols"] = [
      { wch: 28 }, { wch: 20 }, { wch: 28 }, { wch: 18 },
      { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(wb, wsTp, T("Conventions tiers"));

    // Export
    XLSX.writeFile(wb, `${T("rapport-CA-")}${reportYear}-${(foundationName || T("fondation")).replace(/\s+/g, "-")}.xlsx`);
    return snapshot;
  }

  // ── Handler principal : génère les 3 formats et sauvegarde en base ─────────
  async function handleGenerate(format) {
    try {
      setIsGenerating(true);
      setError(null);
      setSuccess(null);

      let snapshot;
      if (format === "pdf")   snapshot = await generatePDF();
      if (format === "word")  snapshot = await generateWord();
      if (format === "excel") snapshot = await generateExcel();
      if (format === "all") {
        snapshot = await generatePDF();
        await generateWord();
        await generateExcel();
      }

      if (snapshot) {
        await saveReportRecord(snapshot, null, "final");
        await loadData(); // rafraîchir la liste des rapports passés
        setSuccess(format === "all"
          ? T("Rapport généré en PDF, Word et Excel. Enregistré dans l'historique.")
          : T("Rapport {f} généré et enregistré.", { f: format.toUpperCase() })
        );
      }
    } catch (e) {
      console.error("[FoundationReport] generate error:", e);
      setError(T("Erreur lors de la génération du rapport : {m}", { m: e?.message || T("Erreur inconnue") }));
    } finally {
      setIsGenerating(false);
    }
  }

  // ── Rendu ──────────────────────────────────────────────────────────────────

  const totalWorks = Object.values(artworksByCol).reduce((s, aws) => s + aws.length, 0);
  const totalValue = collections
    .filter(c => c.collection_type === "foundation_owned")
    .reduce((s, col) => {
      const aws = artworksByCol[col.id] || [];
      return s + aws.reduce((ss, a) => ss + Number(
        a.current_estimated_value || a.valuation_consensus ||
        a.valuation_market_value  || a.estimated_value || 0
      ), 0);
    }, 0);

  const S = {
    page: { maxWidth: 960, margin: "0 auto", padding: "40px 32px", fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif", background: "#F9F7F4", minHeight: "100vh" },
    eyebrow: { fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#9C9C9C", marginBottom: 8 },
    h1: { fontFamily: "Georgia, serif", fontSize: 28, fontWeight: "normal", color: "#1A1A1A", marginBottom: 4 },
    section: { background: "#FFFFFF", border: "1px solid #E5E2DD", padding: "28px 32px", marginBottom: 20 },
    sectionTitle: { fontFamily: "Georgia, serif", fontSize: 16, fontWeight: "normal", color: "#1A1A1A", marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid #F2EFE9" },
    label: { display: "block", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6B6B6B", marginBottom: 6 },
    input: { width: "100%", padding: "10px 14px", border: "1px solid #E5E2DD", background: "#fff", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" },
    textarea: { width: "100%", padding: "10px 14px", border: "1px solid #E5E2DD", background: "#fff", fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", minHeight: 100, boxSizing: "border-box" },
    btnPrimary: { padding: "11px 22px", background: "#1A1A1A", color: "#fff", border: "none", fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" },
    btnGold: { padding: "11px 22px", background: "#C9A961", color: "#fff", border: "none", fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" },
    btnGhost: { padding: "11px 22px", background: "transparent", color: "#1A1A1A", border: "1px solid #E5E2DD", fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit" },
    statCard: { background: "#F9F7F4", border: "1px solid #E5E2DD", padding: "20px 24px", flex: 1 },
    statLabel: { fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "#9C9C9C", marginBottom: 8 },
    statValue: { fontFamily: "Georgia, serif", fontSize: 22, color: "#1A1A1A" },
    badge: (type) => ({
      display: "inline-block", padding: "3px 10px", fontSize: 9, letterSpacing: "0.15em",
      textTransform: "uppercase",
      background: { foundation_owned: "#EBF3EF", foundation_founder: "#FAF8F3", foundation_donation: "#F5ECE5", foundation_deposit: "#E5EBF3" }[type] || "#F9F7F4",
      color: { foundation_owned: "#2C5F4A", foundation_founder: "#7A5C00", foundation_donation: "#8B4513", foundation_deposit: "#1C3A5E" }[type] || "#1A1A1A",
      border: `1px solid`,
      borderColor: { foundation_owned: "#B8D9CB", foundation_founder: "#E8D9B0", foundation_donation: "#D9B89A", foundation_deposit: "#A8BDD9" }[type] || "#E5E2DD",
    }),
  };

  if (isLoading && !isCorporateMode) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 36, height: 36, border: "3px solid #E5E2DD", borderTopColor: "#1A1A1A", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
        <p style={{ color: "#6B6B6B", fontSize: 13 }}>{T("Chargement des données...")}</p>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // UI MODE CORPORATE (Art Advisor → client entreprise)
  // ══════════════════════════════════════════════════════════════════════════
  if (isCorporateMode) {
    const LIEU_OPTIONS = ["Bureau", "Salle de réunion", "Hall / Réception", "Couloir", "Salle de direction", "Showroom", "Réserve", "Autre"];
    const STATUT_OPTIONS = ["Propriété entreprise", "Prêt", "Dépôt", "Donation", "Œuvre en commande"];
    const USAGE_OPTIONS = ["Décoratif", "Patrimonial", "Mécénat", "Exposition publique", "Communication", "Autre"];
    const VISI_OPTIONS = ["Interne", "Clients", "Public", "Institutionnel"];

    const totalEstimVal = corpArtworks.reduce((s, a) => s + getCorpEstVal(a), 0);

    return (
      <div style={S.page}>
        {/* En-tête */}
        <div style={{ marginBottom: 36 }}>
          <div style={S.eyebrow}>{T("Art Advisor — Clients Corporate")}</div>
          <h1 style={S.h1}>{T("Diagnostic Collection Corporate")}</h1>
          <p style={{ fontSize: 13, color: "#6B6B6B", marginTop: 4 }}>
            {T("Audit, inventaire, champs corporate, rapport curatorial — pour fondations d'entreprise, cabinets, hôtels, banques, maisons de luxe.")}
          </p>
        </div>

        {/* Messages */}
        {corpError && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderLeft: "3px solid #EF4444", padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#991B1B" }}>
            {corpError}
          </div>
        )}
        {corpSuccess && (
          <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderLeft: "3px solid #2C5F4A", padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#065F46" }}>
            {corpSuccess}
          </div>
        )}

        {/* Paramètres mission */}
        <div style={S.section}>
          <div style={S.sectionTitle}>{T("Paramètres de la mission")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={S.label}>{T("Nom du client / entreprise")}</label>
              <input type="text" value={corpClientName} onChange={e => setCorpClientName(e.target.value)}
                placeholder={T("Ex. : Groupe Artémis, Hôtel Le Meurice…")} style={S.input} />
            </div>
            <div>
              <label style={S.label}>{T("Art Advisor")}</label>
              <input type="text" value={corpAdvisorName} onChange={e => setCorpAdvisorName(e.target.value)}
                placeholder={T("Votre nom")} style={S.input} />
            </div>
            <div>
              <label style={S.label}>{T("Collection à auditer")}</label>
              <select value={selectedColId} onChange={e => setSelectedColId(e.target.value)} style={{ ...S.input, background: "#fff" }}>
                {allCollections.length === 0
                  ? <option value="">{T("Aucune collection")}</option>
                  : allCollections.map(c => <option key={c.id} value={c.id}>{c.title}</option>)
                }
              </select>
            </div>
          </div>
          <div>
            <label style={S.label}>{T("Note de l'Art Advisor (optionnel — figurera dans le rapport)")}</label>
            <textarea value={corpAuditNotes} onChange={e => setCorpAuditNotes(e.target.value)}
              placeholder={T("Contexte de la mission, observations générales, points d'attention…")}
              style={S.textarea} />
          </div>
        </div>

        {/* Stats rapides */}
        {corpArtworks.length > 0 && (
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { label: T("Oeuvres"), value: corpArtworks.length },
              { label: T("Valeur estimée"), value: totalEstimVal > 0 ? fmtEurUI(totalEstimVal) : "—" },
              { label: T("Champs renseignés"), value: `${corpArtworks.filter(a => getCorpField(a.id, "statut_juridique")).length} / ${corpArtworks.length}` },
              { label: T("États connus"), value: corpArtworks.filter(a => a.condition && a.condition !== "unknown").length },
            ].map(s => (
              <div key={s.label} style={S.statCard}>
                <div style={S.statLabel}>{s.label}</div>
                <div style={S.statValue}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tableau œuvres avec champs corporate */}
        {corpLoading ? (
          <div style={{ ...S.section, textAlign: "center", color: "#9C9C9C" }}>{T("Chargement des œuvres…")}</div>
        ) : corpArtworks.length === 0 ? (
          <div style={{ ...S.section, color: "#9C9C9C" }}>
            {selectedColId ? T("Aucune œuvre dans cette collection.") : T("Sélectionnez une collection.")}
          </div>
        ) : (
          <div style={S.section}>
            <div style={S.sectionTitle}>
              {T("Inventaire & champs corporate")}
              <span style={{ fontSize: 11, color: "#9C9C9C", marginLeft: 12 }}>
                {T("{n} oeuvre(s) — renseignez les 4 champs pour chaque oeuvre", { n: corpArtworks.length })}
              </span>
              {corpSave !== "idle" && (
                <span style={{ fontSize: 11, marginLeft: 12, color: corpSave === "error" ? "#B4572F" : "#9C9C9C" }}>
                  {corpSave === "saving" ? T("Enregistrement…") : corpSave === "saved" ? T("Enregistré") : T("Non enregistré en base (copie conservée dans ce navigateur)")}
                </span>
              )}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #1A1A1A" }}>
                    {["N° inv.", "Titre", "Artiste", "An.", "Lieu d'accrochage ↓", "Statut juridique ↓", "Usage ↓", "Visibilité ↓", "État", "Valeur"].map((x) => T(x)).map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "#6B6B6B", fontWeight: "normal", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {corpArtworks.map((aw, idx) => (
                    <tr key={aw.id} style={{ borderBottom: "1px solid #F2EFE9", background: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                      <td style={{ padding: "6px 10px", color: "#9C9C9C", fontSize: 11 }}>{aw.inventory_number || "—"}</td>
                      <td style={{ padding: "6px 10px", maxWidth: 160 }}>
                        <div style={{ fontWeight: 500, color: "#1A1A1A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{aw.title || T("Sans titre")}</div>
                      </td>
                      <td style={{ padding: "6px 10px", color: "#6B6B6B", whiteSpace: "nowrap" }}>{aw.artist || "—"}</td>
                      <td style={{ padding: "6px 10px", color: "#9C9C9C" }}>{aw.year || "—"}</td>

                      {/* Lieu d'accrochage */}
                      <td style={{ padding: "4px 6px" }}>
                        <select value={getCorpField(aw.id, "lieu_accrochage")}
                          onChange={e => updateCorpField(aw.id, "lieu_accrochage", e.target.value)}
                          style={{ ...S.input, padding: "4px 8px", fontSize: 11, width: "auto", minWidth: 120, border: getCorpField(aw.id, "lieu_accrochage") ? "1px solid #C9A961" : "1px solid #E5E2DD" }}>
                          <option value="">{T("— choisir —")}</option>
                          {LIEU_OPTIONS.map(o => <option key={o} value={o}>{T(o)}</option>)}
                        </select>
                      </td>

                      {/* Statut juridique */}
                      <td style={{ padding: "4px 6px" }}>
                        <select value={getCorpField(aw.id, "statut_juridique")}
                          onChange={e => updateCorpField(aw.id, "statut_juridique", e.target.value)}
                          style={{ ...S.input, padding: "4px 8px", fontSize: 11, width: "auto", minWidth: 140, border: getCorpField(aw.id, "statut_juridique") ? "1px solid #C9A961" : "1px solid #E5E2DD" }}>
                          <option value="">{T("— choisir —")}</option>
                          {STATUT_OPTIONS.map(o => <option key={o} value={o}>{T(o)}</option>)}
                        </select>
                      </td>

                      {/* Usage */}
                      <td style={{ padding: "4px 6px" }}>
                        <select value={getCorpField(aw.id, "usage")}
                          onChange={e => updateCorpField(aw.id, "usage", e.target.value)}
                          style={{ ...S.input, padding: "4px 8px", fontSize: 11, width: "auto", minWidth: 120, border: getCorpField(aw.id, "usage") ? "1px solid #C9A961" : "1px solid #E5E2DD" }}>
                          <option value="">{T("— choisir —")}</option>
                          {USAGE_OPTIONS.map(o => <option key={o} value={o}>{T(o)}</option>)}
                        </select>
                      </td>

                      {/* Visibilité */}
                      <td style={{ padding: "4px 6px" }}>
                        <select value={getCorpField(aw.id, "visibilite")}
                          onChange={e => updateCorpField(aw.id, "visibilite", e.target.value)}
                          style={{ ...S.input, padding: "4px 8px", fontSize: 11, width: "auto", minWidth: 110, border: getCorpField(aw.id, "visibilite") ? "1px solid #C9A961" : "1px solid #E5E2DD" }}>
                          <option value="">{T("— choisir —")}</option>
                          {VISI_OPTIONS.map(o => <option key={o} value={o}>{T(o)}</option>)}
                        </select>
                      </td>

                      <td style={{ padding: "6px 10px" }}>
                        <span style={{
                          fontSize: 9, padding: "2px 7px", textTransform: "uppercase", letterSpacing: "0.1em",
                          background: ["excellent", "very_good", "good"].includes(aw.condition) ? "#EBF3EF"
                            : ["fair"].includes(aw.condition) ? "#FEF9EC"
                            : ["poor", "damaged"].includes(aw.condition) ? "#FEF2F2" : "#F9F7F4",
                          color: ["excellent", "very_good", "good"].includes(aw.condition) ? "#2C5F4A"
                            : ["fair"].includes(aw.condition) ? "#92400E"
                            : ["poor", "damaged"].includes(aw.condition) ? "#991B1B" : "#6B6B6B",
                        }}>
                          {aw.condition || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap", color: getCorpEstVal(aw) ? "#1A1A1A" : "#C8C4BE" }}>
                        {getCorpEstVal(aw) ? fmtEurUI(getCorpEstVal(aw)) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 11, color: "#9C9C9C", marginTop: 12 }}>
              {T("Les champs encadrés en doré sont renseignés. Ils sont enregistrés automatiquement dans ARTICHAUT.")}
            </p>
          </div>
        )}

        {/* Boutons d'export */}
        <div style={S.section}>
          <div style={S.sectionTitle}>{T("Générer les rapports")}</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <button
              style={{ ...S.btnGold, opacity: corpGenerating ? 0.6 : 1 }}
              onClick={() => handleCorporateGenerate("all")}
              disabled={corpGenerating || corpArtworks.length === 0}
            >
              {corpGenerating ? T("Génération en cours…") : T("Tout générer (Audit + Curatorial + Excel)")}
            </button>
            <button style={{ ...S.btnGhost, opacity: corpGenerating ? 0.6 : 1 }}
              onClick={() => handleCorporateGenerate("audit")} disabled={corpGenerating || corpArtworks.length === 0}>
              {T("PDF Audit Corporate")}
            </button>
            <button style={{ ...S.btnGhost, opacity: corpGenerating ? 0.6 : 1 }}
              onClick={() => handleCorporateGenerate("curatorial")} disabled={corpGenerating || corpArtworks.length === 0}>
              {T("PDF Cohérence Curatoriale")}
            </button>
            <button style={{ ...S.btnGhost, opacity: corpGenerating ? 0.6 : 1 }}
              onClick={() => handleCorporateGenerate("excel")} disabled={corpGenerating || corpArtworks.length === 0}>
              {T("Excel Corporate")}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "#6B6B6B", lineHeight: 1.6 }}>
            <strong>{T("PDF Audit Corporate")}</strong> — {T("Diagnostic complet : inventaire, champs corporate, état documentaire, risques, recommandations assurance & conservation. À remettre au DG, direction com ou mécénat.")}<br />
            <strong>{T("PDF Cohérence Curatoriale")}</strong> — {T("Analyse de la ligne artistique, répartition par médium/artiste, recommandations d'acquisition. Pour montrer qu'il y a une vraie stratégie.")}<br />
            <strong>Excel</strong> — {T("Export complet avec les 4 champs corporate + synthèses par usage et lieu, pour la direction financière ou juridique.")}
          </div>
        </div>

      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // UI MODE FONDATION (rapport annuel CA — comportement historique)
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div style={S.page}>

      {/* En-tête */}
      <div style={{ marginBottom: 36 }}>
        <div style={S.eyebrow}>{T("Mode Fondation — Rapport institutionnel")}</div>
        <h1 style={S.h1}>{T("Rapport annuel — Conseil d'Administration")}</h1>
        <p style={{ fontSize: 13, color: "#6B6B6B", marginTop: 4 }}>
          {T("Générez l'inventaire signé, la valorisation et l'état de conservation pour le CA.")}
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderLeft: "3px solid #EF4444", padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#991B1B" }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderLeft: "3px solid #2C5F4A", padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#065F46" }}>
          {success}
        </div>
      )}

      {/* Stats panorama */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        {[
          { label: T("Total oeuvres"), value: totalWorks },
          { label: T("Collections"), value: collections.length },
          { label: T("Valeur propriété propre"), value: fmtEurUI(totalValue) },
          { label: T("Conventions actives"), value: Object.keys(thirdParties).length },
        ].map(s => (
          <div key={s.label} style={S.statCard}>
            <div style={S.statLabel}>{s.label}</div>
            <div style={S.statValue}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Formulaire */}
      <div style={S.section}>
        <div style={S.sectionTitle}>{T("Paramètres du rapport")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20, marginBottom: 20 }}>
          <div>
            <label style={S.label}>{T("Exercice (année)")}</label>
            <input
              type="number"
              value={reportYear}
              onChange={e => setReportYear(Number(e.target.value))}
              min={2020} max={2100}
              style={S.input}
            />
          </div>
          <div>
            <label style={S.label}>{T("Nom de la fondation")}</label>
            <input
              type="text"
              value={foundationName}
              onChange={e => setFoundationName(e.target.value)}
              placeholder={T("Ex. : Fondation Lumières")}
              style={S.input}
            />
          </div>
          <div>
            <label style={S.label}>{T("Nom du directeur")}</label>
            <input
              type="text"
              value={directorName}
              onChange={e => setDirectorName(e.target.value)}
              placeholder={T("Prénom Nom")}
              style={S.input}
            />
          </div>
          <div>
            <label style={S.label}>{T("Logo (optionnel)")}</label>
            <button type="button" style={S.btnGhost} onClick={() => logoInputRef.current?.click()}>
              {foundationLogo ? T("Logo chargé ✓") : T("Choisir un fichier…")}
            </button>
            <input ref={logoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoUpload} />
          </div>
        </div>
        <div>
          <label style={S.label}>{T("Note du directeur pour le CA (optionnel)")}</label>
          <textarea
            value={directorNote}
            onChange={e => setDirectorNote(e.target.value)}
            placeholder={T("Commentaires, orientations, points d'attention pour le Conseil d'Administration...")}
            style={S.textarea}
          />
        </div>
      </div>

      {/* Collections incluses */}
      <div style={S.section}>
        <div style={S.sectionTitle}>
          {T("Collections incluses")}
          <span style={{ fontSize: 11, color: "#9C9C9C", marginLeft: 12 }}>{T("{c} collection(s) — {w} oeuvre(s)", { c: collections.length, w: totalWorks })}</span>
        </div>
        {collections.length === 0 ? (
          <p style={{ color: "#9C9C9C", fontSize: 13 }}>{T("Aucune collection de type Fondation trouvée. Créez des collections avec les types foundation_* dans la gestion de collections.")}</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
            {collections.map(col => {
              const aws = artworksByCol[col.id] || [];
              const tp  = thirdParties[col.id];
              return (
                <div key={col.id} style={{ border: "1px solid #E5E2DD", padding: "16px 18px", background: "#FAFAFA" }}>
                  <div style={{ marginBottom: 8 }}><span style={S.badge(col.collection_type)}>{COLLECTION_TYPE_LABELS[col.collection_type] ? T(COLLECTION_TYPE_LABELS[col.collection_type]) : col.collection_type}</span></div>
                  <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1A1A1A", marginBottom: 4 }}>{col.title}</div>
                  <div style={{ fontSize: 12, color: "#6B6B6B" }}>{T("{n} oeuvre(s)", { n: aws.length })}</div>
                  {tp && <div style={{ fontSize: 11, color: "#9C9C9C", marginTop: 4 }}>{T("Tiers : {p}", { p: tp.party_name })}</div>}
                  {tp?.end_date && (
                    <div style={{ fontSize: 11, color: new Date(tp.end_date) < new Date() ? "#EF4444" : "#C9A961", marginTop: 2 }}>
                      {T("Échéance : {d}", { d: fmtDateUI(tp.end_date) })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Boutons export */}
      <div style={S.section}>
        <div style={S.sectionTitle}>{T("Générer le rapport")}</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <button
            style={{ ...S.btnGold, opacity: isGenerating ? 0.6 : 1 }}
            onClick={() => handleGenerate("all")}
            disabled={isGenerating || collections.length === 0}
          >
            {isGenerating ? T("Génération en cours…") : T("Générer PDF + Word + Excel")}
          </button>
          <button style={{ ...S.btnGhost, opacity: isGenerating ? 0.6 : 1 }} onClick={() => handleGenerate("pdf")} disabled={isGenerating || collections.length === 0}>PDF</button>
          <button style={{ ...S.btnGhost, opacity: isGenerating ? 0.6 : 1 }} onClick={() => handleGenerate("word")} disabled={isGenerating || collections.length === 0}>Word</button>
          <button style={{ ...S.btnGhost, opacity: isGenerating ? 0.6 : 1 }} onClick={() => handleGenerate("excel")} disabled={isGenerating || collections.length === 0}>Excel</button>
        </div>
        <p style={{ fontSize: 11, color: "#9C9C9C" }}>
          {T("Le rapport est automatiquement enregistré dans l'historique après génération. Un seul rapport final peut exister par exercice.")}
        </p>
      </div>

      {/* Rapports passés */}
      {pastReports.length > 0 && (
        <div style={S.section}>
          <div style={S.sectionTitle}>{T("Historique des rapports")}</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #E5E2DD" }}>
                {["Exercice", "Généré le", "Oeuvres", "Valeur", "Statut"].map((x) => T(x)).map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "#9C9C9C", fontWeight: "normal" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pastReports.map((r, i) => (
                <tr key={r.id} style={{ borderBottom: "1px solid #F2EFE9", background: i % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                  <td style={{ padding: "10px 12px", fontFamily: "Georgia, serif", fontSize: 15 }}>{r.year}</td>
                  <td style={{ padding: "10px 12px", color: "#6B6B6B" }}>{fmtDateUI(r.generated_at)}</td>
                  <td style={{ padding: "10px 12px" }}>{r.total_works ?? "—"}</td>
                  <td style={{ padding: "10px 12px" }}>{r.total_value_eur ? fmtEurUI(r.total_value_eur) : "—"}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{
                      fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", padding: "2px 8px",
                      background: r.status === "final" || r.status === "submitted_to_board" ? "#EBF3EF" : "#F9F7F4",
                      color: r.status === "final" || r.status === "submitted_to_board" ? "#2C5F4A" : "#6B6B6B",
                      border: "1px solid",
                      borderColor: r.status === "final" || r.status === "submitted_to_board" ? "#B8D9CB" : "#E5E2DD",
                    }}>
                      {{ draft: T("Brouillon"), final: T("Final"), submitted_to_board: T("Soumis CA") }[r.status] || r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
